import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MagnifyingGlass, ShoppingCart, User, List, CaretDown, CaretRight, Microphone, Package, Receipt, Truck, Headset, SignOut, Fire, Lightning, TrendUp, Clock, X, CurrencyCircleDollar, LinkSimple, HeartStraight, Gift, SquaresFour, Bell, Sparkle, MapPinLine, ChatCircleDots, Camera, CircleNotch, DotsThree, Storefront } from '@phosphor-icons/react'
import { AiSearchPanel } from '@/components/AiSearchPanel'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { VehicleSearchPanel } from '@/components/search/VehicleSearchPanel'
import type { AccountNotification } from '@/hooks/use-account-notifications'
import { useIsMobile } from '@/hooks/use-mobile'
import { useVoiceSearch } from '@/hooks/use-voice-search'
import { useSearchAutocomplete } from '@/hooks/use-search-autocomplete'
import { useLocalStorage } from '@/hooks/use-local-storage'
import { useStorefrontSiteConfig } from '@/contexts/site-config'
import { usePricingContext } from '@/components/pricing/PricingContext'
import { toast } from 'sonner'
import { buildProductHref, handleLinkClick, MARKETPLACE_ACCOUNT_HOME_PATH, VIEW_PATHS } from '@/lib/navigation'
import { isProductDisplayedPricePending, resolveProductPricing } from '@/lib/pricing'
import { resolveProductStockDisplay } from '@/lib/product-stock'
import { getContrastRatio, hexToRgba, isHexColor, resolveContrastColor } from '@/lib/storefront-theme'
import { cn } from '@/lib/utils'
import { menuAppsForVisitor, type AppSegmentKey } from '@/lib/apps-catalog'
import consultaengIcon from '@/assets/apps/consultaeng.png'
import universidadeIcon from '@/assets/apps/universidadev2.png'
import type { Category, CategorySelectionData, Product, VehicleFilter } from '@/lib/types'
import { getMarketplaceVitrineSellerPerguntas } from '@/lib/api-shell'
import { useTranslation } from 'react-i18next'
import { formatCommerceCurrency } from '@/lib/commerce-context'
import { queueStorefrontAnalyticsEvent, type StorefrontSearchResultContext } from '@/lib/storefront-analytics'
import { getStoreNetworkJourneyId } from '@/lib/store-network'
import { formatSearchHighlightLabel } from '@/lib/search-highlight'

type VehicleSearchMode = 'plate' | 'manual'

interface HeaderProps {
  cartCount: number
  isLoggedIn: boolean
  priceTableHref?: string
  canAccessIndicatorPortal?: boolean
  canAccessBusiness?: boolean
  canAccessAffiliates?: boolean
  canAccessMarketplace?: boolean
  storeNetworkMarketplaceEnabled?: boolean
  storeNetworkOriginGlobalStoreId?: string
  accountNotifications?: AccountNotification[]
  authenticatedSearchHighlights?: Array<{
    label: string
    badge?: string
  }>
  suppressGuestSearchHighlights?: boolean
  accountProfile?: {
    corporateName?: string
    name?: string
    email?: string
    cnpj?: string
    entidadeId?: number
    electricStudioEnabled?: boolean
    buyerUser?: {
      nome?: string
      tipoUsuario?: 'admin' | 'vendedor'
      papel: 'admin' | 'vendedor'
      acessoFinanceiro?: boolean
      acessoPedidos?: boolean
      acessoMarketplaceAnuncios?: boolean
      acessoMarketplacePerguntas?: boolean
    }
  } | null
  indicatorDelegation?: {
    indicatorId?: number
    indicatorName: string
    indicatorDocument: string
    clientName: string
    clientDocument: string
    authorized: boolean
  } | null
  activeSearchQuery?: string
  activeVehicleFilter?: VehicleFilter | null
  currentView: string
  navigationKey: string
  categories: Category[]
  isMenuLoading?: boolean
  isImageSearchLoading?: boolean
  isImageSearchActive?: boolean
  onSearchChange: (query: string) => void
  onVehicleSearch?: (params: { query: string; vehicle: VehicleFilter }) => void | Promise<void>
  onImageSearch?: (file: File) => Promise<void> | void
  // Busca por IA: sem loja_id configurado o botão nem aparece.
  aiSearchLojaId?: number | null
  onAiAddToCart?: (product: Product, searchContext?: StorefrontSearchResultContext | null) => void
  onProductClick: (product: Product) => void
  onLoginClick: () => void
  onCartClick: () => void
  onCartPrefetch?: () => void
  onSalesCampaignPrefetch?: () => void
  onCategoriesClick: () => void
  onCategoriesPrefetch?: () => void
  onCategorySelect: (categoryId: string, categoryData?: CategorySelectionData) => void
  onViewChange: (view: string) => void
  onLogout: () => void
  onEndIndicatorDelegation?: () => void
  initialVehicleSearchMode?: VehicleSearchMode
}

type HeaderQuickLinkItem = {
  key: string
  kind: 'categories' | 'default'
  icon: typeof Sparkle
  href: string
  label: string
  isActive: boolean
  onClick: () => void
  openInNewTab?: boolean
  itemClassName?: string
}

type MobileSearchPanelFrame = {
  top: number
  left: number
  width: number
}

const FALLBACK_MOST_SEARCHED_KEYS = ['breakers', 'contactors', 'plcs', 'sensors', 'cables', 'transformers'] as const
const IMAGE_SEARCH_MAX_FILE_BYTES = 10 * 1024 * 1024
const IMAGE_SEARCH_ALLOWED_FILE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp'])
const SEARCH_RESULT_CURRENCY_FORMATTER = { format: formatCommerceCurrency }
const areStringArraysEqual = (current: string[] | null, next: string[]) => {
  if (!current) return false
  if (current.length !== next.length) return false
  return current.every((value, index) => value === next[index])
}

const DYNAMIC_SEARCH_HIGHLIGHT_ICONS = [Fire, Lightning, TrendUp, Clock] as const
const resolveSearchHighlightIconClassName = (icon: typeof Fire) => {
  if (icon === Fire) return 'text-[#dc2626]'
  if (icon === Lightning) return 'text-[#eab308]'
  if (icon === TrendUp) return 'text-[#16a34a]'
  return 'text-accent'
}

function SearchProductResultStatus({ product, compact = false }: { product: Product; compact?: boolean }) {
  const { t } = useTranslation()
  const pricingContext = usePricingContext()
  const pricing = resolveProductPricing(product, pricingContext)
  const isPricePending = isProductDisplayedPricePending(product, pricingContext, pricing)
  const stockDisplay = resolveProductStockDisplay(
    product,
    pricingContext.showRealStock,
    pricingContext.stockStatusMode,
  )
  const visiblePrice =
    stockDisplay.available
    && !isPricePending
    && pricing.canShowPrice
    && typeof pricing.actualPrice === 'number'
    && Number.isFinite(pricing.actualPrice)
    && pricing.actualPrice > 0
      ? pricing.actualPrice
      : null
  const showPricePendingStatus =
    stockDisplay.available
    && pricingContext.showPrices
    && isPricePending
  const showPriceAfterLogin = stockDisplay.available && !pricingContext.showPrices

  return (
    <div className={cn(
      'flex min-h-5 flex-wrap items-end justify-between gap-y-1',
      compact
        ? 'mt-2 gap-x-1.5 border-t border-border/50 pt-1.5'
        : 'mt-auto gap-x-3 pt-1',
    )}>
      <div>
        {stockDisplay.visible ? (
          <Badge className={`rounded-full px-1.5 py-0 ${compact ? 'text-[9px]' : 'text-[10px]'} ${
            stockDisplay.available
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}>
            {stockDisplay.label}
          </Badge>
        ) : null}
      </div>

      {visiblePrice !== null ? (
        <div className="ml-auto flex items-baseline gap-1 text-right">
          <span className={cn('font-medium text-muted-foreground', compact ? 'text-[9px]' : 'text-[10px]')}>
            {product.hasVariations ? t('product.price_from') : t('product.price_for')}
          </span>
          <span className={cn('font-price font-bold tabular-nums text-accent', compact ? 'text-[13px]' : 'text-sm')}>
            {SEARCH_RESULT_CURRENCY_FORMATTER.format(visiblePrice)}
          </span>
        </div>
        ) : showPricePendingStatus ? (
        <div
          role="status"
          aria-label={t('header.search.loading')}
          className="ml-auto flex animate-pulse flex-col items-end gap-1"
        >
          <span className="h-1.5 w-10 rounded bg-muted" />
          <span className="h-3.5 w-20 rounded bg-muted" />
        </div>
      ) : showPriceAfterLogin ? (
        <span className="ml-auto text-[10px] font-medium text-muted-foreground">
          {t('product.price_after_login')}
        </span>
      ) : null}
    </div>
  )
}

const CategoryMegaMenu = lazy(() =>
  import('./CategoryMegaMenu').then((module) => ({ default: module.CategoryMegaMenu }))
)
const VoiceWaveform = lazy(() =>
  import('./VoiceWaveform').then((module) => ({ default: module.VoiceWaveform }))
)
const SearchDropdown = lazy(() =>
  import('@/components/search/SearchDropdown').then((module) => ({ default: module.SearchDropdown }))
)
const AccountCustomerLogo = lazy(() =>
  import('@/components/account/AccountCustomerLogo').then((module) => ({ default: module.AccountCustomerLogo }))
)

export function Header({
  cartCount,
  isLoggedIn,
  priceTableHref = VIEW_PATHS['price-table'],
  canAccessIndicatorPortal = false,
  canAccessBusiness = false,
  canAccessAffiliates = false,
  canAccessMarketplace = false,
  storeNetworkMarketplaceEnabled = false,
  storeNetworkOriginGlobalStoreId,
  accountNotifications = [],
  authenticatedSearchHighlights = [],
  suppressGuestSearchHighlights = false,
  accountProfile = null,
  indicatorDelegation = null,
  activeSearchQuery = '',
  activeVehicleFilter = null,
  currentView,
  navigationKey,
  categories,
  isMenuLoading = false,
  isImageSearchLoading = false,
  isImageSearchActive = false,
  onSearchChange,
  onVehicleSearch,
  onImageSearch,
  aiSearchLojaId = null,
  onAiAddToCart,
  onProductClick,
  onLoginClick,
  onCartClick,
  onCartPrefetch,
  onSalesCampaignPrefetch,
  onCategoriesClick,
  onCategoriesPrefetch,
  onCategorySelect,
  onViewChange,
  onLogout,
  onEndIndicatorDelegation,
  initialVehicleSearchMode = 'plate',
}: HeaderProps) {
  const { t } = useTranslation()
  const {
    siteConfig,
    siteName,
    primaryColor,
    secondaryColor,
    popularSearches,
    searchHighlights,
    showAppsMenu,
    showVehiclePlateFilter,
  } = useStorefrontSiteConfig()
  const showCommunityLinks = siteConfig?.experiencia_config?.recursos.comunidade !== false
  const showTechnicalTablesLink = showCommunityLinks && Number(siteConfig?.loja_id) === 101
  const [searchQuery, setSearchQuery] = useState('')
  const [showMegaMenu, setShowMegaMenu] = useState(false)
  const [showMobileCategoryMenu, setShowMobileCategoryMenu] = useState(false)
  const [showAccountMenu, setShowAccountMenu] = useState(false)
  const [showMobileAccountSheet, setShowMobileAccountSheet] = useState(false)
  const [showMobileAppsSheet, setShowMobileAppsSheet] = useState(false)
  const [showVehicleSearchPopover, setShowVehicleSearchPopover] = useState(false)
  const [showSearchDropdown, setShowSearchDropdown] = useState(false)
  const [showEnhancedDropdown, setShowEnhancedDropdown] = useState(false)
  const [mobileSearchPanelFrame, setMobileSearchPanelFrame] = useState<MobileSearchPanelFrame | null>(null)
  const [currentTermIndex, setCurrentTermIndex] = useState(0)
  const [marketplacePendingQuestions, setMarketplacePendingQuestions] = useState(0)
  const [activeAppSegment, setActiveAppSegment] = useState<AppSegmentKey>('featured')
  const [appSearchQuery, setAppSearchQuery] = useState('')
  const [recentlyViewed] = useLocalStorage<Product[]>('recently-viewed', [])
  const [searchHistory, setSearchHistory] = useLocalStorage<string[]>('search-history', [])
  const isMobile = useIsMobile()
  const searchRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const imageInputRef = useRef<HTMLInputElement | null>(null)
  const autocompleteLoadMoreRef = useRef<HTMLDivElement | null>(null)
  const quickLinksLayoutRef = useRef<HTMLDivElement | null>(null)
  const quickLinksMeasureListRef = useRef<HTMLUListElement | null>(null)
  const quickLinkMeasureItemRefs = useRef<Record<string, HTMLLIElement | null>>({})
  const vehicleQuickLinkRef = useRef<HTMLDivElement | null>(null)
  const appsQuickLinkRef = useRef<HTMLDivElement | null>(null)
  const moreOptionsMeasureRef = useRef<HTMLButtonElement | null>(null)
  const [renderedBaseQuickLinkKeys, setRenderedBaseQuickLinkKeys] = useState<string[] | null>(null)
  const [visibleBaseQuickLinkKeys, setVisibleBaseQuickLinkKeys] = useState<string[] | null>(null)
  const loggedBuyerUserName = accountProfile?.buyerUser?.nome?.trim() || ''
  const isSellerAccount = accountProfile?.buyerUser?.tipoUsuario === 'vendedor'
  const shouldShowAccountNotifications = !isSellerAccount
  const visibleAccountNotifications = shouldShowAccountNotifications ? accountNotifications : []
  const loggedAccountName = indicatorDelegation?.indicatorName?.trim()
    || loggedBuyerUserName
    || accountProfile?.name?.trim()
    || t('header.account.default_name')
  const loggedAccountCorporateNameRaw = indicatorDelegation ? '' : accountProfile?.corporateName?.trim() || ''
  const loggedAccountCorporateName = loggedAccountCorporateNameRaw
    && loggedAccountCorporateNameRaw.toLocaleLowerCase() !== loggedAccountName.toLocaleLowerCase()
      ? loggedAccountCorporateNameRaw
      : ''
  const loggedAccountDocumentDigits = (indicatorDelegation?.indicatorDocument || accountProfile?.cnpj || '').replace(/\D/g, '')
  const loggedAccountDocument = loggedAccountDocumentDigits.length === 14
    ? loggedAccountDocumentDigits.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*$/,
        '$1.$2.$3/$4-$5'
      )
    : loggedAccountDocumentDigits || t('header.account.cnpj_not_informed')
  const delegatedClientDocumentDigits = (indicatorDelegation?.clientDocument || '').replace(/\D/g, '')
  const delegatedClientDocument = delegatedClientDocumentDigits.length === 14
    ? delegatedClientDocumentDigits.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*$/,
        '$1.$2.$3/$4-$5'
      )
    : delegatedClientDocumentDigits
  const accountMenuItemClassName = 'group relative flex cursor-pointer items-center gap-2 rounded-[12px] px-1.5 py-1.5 text-left text-slate-800 whitespace-normal transition-colors outline-none data-[highlighted]:bg-[#FFF8F0] data-[highlighted]:text-slate-950'
  const accountMenuIconClassName = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-transparent text-slate-500 transition-colors duration-200 group-data-[highlighted]:bg-[#FFF4E5] group-data-[highlighted]:text-[#FF9B00]'
  const headerLogoUrl =
    siteConfig?.logo_url_escuro?.trim() ||
    siteConfig?.logo_url?.trim() || ''
  const fallbackMostSearched = useMemo(
    () => FALLBACK_MOST_SEARCHED_KEYS.map((termKey) => t(`header.search.most.${termKey}`)),
    [t]
  )
  const resolvedMostSearched = popularSearches.length > 0 ? popularSearches : fallbackMostSearched
  const activeSearchHighlights = isLoggedIn
    ? authenticatedSearchHighlights
    : (suppressGuestSearchHighlights ? [] : searchHighlights)
  const resolvedSearchHighlights = useMemo(
    () =>
      activeSearchHighlights.length > 0
        ? activeSearchHighlights.map((term, index) => ({
            icon: DYNAMIC_SEARCH_HIGHLIGHT_ICONS[index % DYNAMIC_SEARCH_HIGHLIGHT_ICONS.length] ?? Fire,
            label: formatSearchHighlightLabel(term.label),
          }))
        : [],
    [activeSearchHighlights]
  )
  const activeSearchHighlight = resolvedSearchHighlights.length > 0
    ? resolvedSearchHighlights[currentTermIndex % resolvedSearchHighlights.length]
    : null
  const ActiveSearchHighlightIcon = activeSearchHighlight?.icon ?? Fire
  useEffect(() => {
    if (resolvedSearchHighlights.length <= 1) {
      setCurrentTermIndex(0)
      return
    }

    const interval = setInterval(() => {
      setCurrentTermIndex((prev) => (prev + 1) % resolvedSearchHighlights.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [resolvedSearchHighlights.length])

  useEffect(() => {
    setShowMegaMenu(false)
    setShowMobileCategoryMenu(false)
    setShowAccountMenu(false)
    setShowMobileAccountSheet(false)
    setShowMobileAppsSheet(false)
    setShowVehicleSearchPopover(false)
  }, [navigationKey])

  useEffect(() => {
    setSearchQuery((current) => (current === activeSearchQuery ? current : activeSearchQuery))
  }, [activeSearchQuery])

  useEffect(() => {
    if (!isLoggedIn || !canAccessMarketplace || !accountProfile?.buyerUser?.acessoMarketplacePerguntas) {
      setMarketplacePendingQuestions(0)
      return
    }
    let cancelled = false
    void getMarketplaceVitrineSellerPerguntas()
      .then((result) => {
        if (!cancelled) setMarketplacePendingQuestions(result.pendentes)
      })
      .catch(() => {
        if (!cancelled) setMarketplacePendingQuestions(0)
      })
    return () => { cancelled = true }
  }, [
    accountProfile?.buyerUser?.acessoMarketplacePerguntas,
    canAccessMarketplace,
    isLoggedIn,
    navigationKey,
  ])

  useEffect(() => {
    const updatePendingQuestions = (event: Event) => {
      const next = Number((event as CustomEvent<{ pendentes?: unknown }>).detail?.pendentes)
      if (Number.isFinite(next)) setMarketplacePendingQuestions(Math.max(0, Math.trunc(next)))
    }
    window.addEventListener('marketplace-vitrine:perguntas-atualizadas', updatePendingQuestions)
    return () => window.removeEventListener('marketplace-vitrine:perguntas-atualizadas', updatePendingQuestions)
  }, [])

  const closeSearchDropdown = useCallback(() => {
    setShowSearchDropdown(false)
    setShowEnhancedDropdown(false)
  }, [])

  // Busca por IA: dropdown próprio ancorado na barra de busca. Sem loja_id
  // configurado, o recurso fica invisível.
  const [isAiSearchOpen, setIsAiSearchOpen] = useState(false)
  const [aiSearchSeed, setAiSearchSeed] = useState<{ query: string; key: number } | null>(null)
  const isAiSearchAvailable = Number(aiSearchLojaId ?? 0) > 0

  // Atalho discreto no dropdown de resultados: abre a busca por IA já rodando
  // com o texto digitado.
  const openAiSearchWithQuery = useCallback(() => {
    if (!isAiSearchAvailable) return
    const query = searchQuery.trim()
    closeSearchDropdown()
    setAiSearchSeed(query ? { query, key: Date.now() } : null)
    setIsAiSearchOpen(true)
  }, [closeSearchDropdown, isAiSearchAvailable, searchQuery])

  const closeMegaMenus = () => {
    setShowMegaMenu(false)
    setShowMobileCategoryMenu(false)
  }

  const closeAccountMenu = () => {
    setShowAccountMenu(false)
    setShowMobileAccountSheet(false)
  }

  const closeAppsMenu = () => {
    setShowMobileAppsSheet(false)
  }

  const closeVehicleSearchPopover = () => {
    setShowVehicleSearchPopover(false)
  }

  const handleAccountLogout = () => {
    closeAccountMenu()
    onLogout()
  }

  const accountMenuItems = [
    {
      key: 'dashboard',
      title: t('account.dashboard.title'),
      description: t('account.dashboard.subtitle'),
      icon: SquaresFour,
      href: VIEW_PATHS.dashboard,
      view: 'dashboard',
      active: currentView === 'dashboard',
    },
    {
      key: 'cart',
      title: t('header.account.cart'),
      description: cartCount > 0 ? t('cart.items_in_cart', { count: cartCount }) : t('cart.view_full_cart'),
      icon: ShoppingCart,
      active: currentView === 'checkout' || currentView === 'cart',
      onClick: onCartClick,
    },
    {
      key: 'orders',
      title: t('header.account.orders'),
      description: t('account.orders.subtitle'),
      icon: Package,
      href: VIEW_PATHS.orders,
      view: 'orders',
      active: currentView === 'orders',
    },
    {
      key: 'quotes',
      title: t('header.account.quotes'),
      description: t('account.quotes.subtitle'),
      icon: Receipt,
      href: VIEW_PATHS.quotes,
      view: 'quotes',
      active: currentView === 'quotes',
    },
    {
      key: 'attendances',
      title: t('header.account.attendances'),
      description: t('account.attendances.subtitle'),
      icon: ChatCircleDots,
      href: VIEW_PATHS.attendances,
      view: 'attendances',
      active: currentView === 'attendances',
    },
    {
      key: 'finance',
      title: t('account.finance.title'),
      description: t('account.finance.subtitle'),
      icon: CurrencyCircleDollar,
      href: VIEW_PATHS.finance,
      view: 'finance',
      active: currentView === 'finance',
    },
    {
      key: 'marketplace',
      title: 'Marketplace',
      description: marketplacePendingQuestions === 1
        ? '1 pergunta pendente'
        : `${marketplacePendingQuestions} perguntas pendentes`,
      icon: Storefront,
      href: accountProfile?.buyerUser?.acessoMarketplaceAnuncios
        ? MARKETPLACE_ACCOUNT_HOME_PATH
        : VIEW_PATHS['marketplace-questions'],
      view: accountProfile?.buyerUser?.acessoMarketplaceAnuncios
        ? 'marketplace-listings'
        : 'marketplace-questions',
      active: currentView === 'marketplace'
        || currentView === 'marketplace-listings'
        || currentView === 'marketplace-questions'
        || currentView === 'marketplace-performance',
    },
    {
      key: 'benefits',
      title: t('header.account.benefits'),
      description: t('account.benefits.subtitle'),
      icon: Gift,
      href: VIEW_PATHS.benefits,
      view: 'benefits',
      active: currentView === 'benefits',
    },
    {
      key: 'business',
      title: t('header.account.business'),
      description: t('header.account.business_description'),
      icon: SquaresFour,
      href: VIEW_PATHS.business,
      view: 'business',
      active: currentView === 'business',
    },
    {
      key: 'addresses',
      title: t('header.account.addresses'),
      description: t('account.addresses.subtitle'),
      icon: MapPinLine,
      href: VIEW_PATHS.addresses,
      view: 'addresses',
      active: currentView === 'addresses',
    },
    {
      key: 'favorites',
      title: t('account.favorites.title'),
      description: t('account.favorites.subtitle'),
      icon: HeartStraight,
      href: VIEW_PATHS.favorites,
      view: 'favorites',
      active: currentView === 'favorites',
    },
    {
      key: 'indicator',
      title: t('account.indicator.title'),
      description: t('account.indicator.subtitle'),
      icon: Bell,
      href: VIEW_PATHS.indicator,
      view: 'indicator',
      active: currentView === 'indicator',
    },
    {
      key: 'affiliates',
      title: t('account.affiliates.title'),
      description: t('account.affiliates.subtitle'),
      icon: LinkSimple,
      href: VIEW_PATHS.affiliates,
      view: 'affiliates',
      active: currentView === 'affiliates',
    },
    {
      key: 'returns',
      title: t('header.account.returns'),
      description: t('account.returns.subtitle'),
      icon: Truck,
      href: VIEW_PATHS.returns,
      view: 'returns',
      active: currentView === 'returns',
    },
    {
      key: 'support',
      title: t('header.account.support'),
      description: t('account.support.subtitle'),
      icon: Headset,
      href: VIEW_PATHS.support,
      view: 'support',
      active: currentView === 'support',
    },
  ].filter((item) => {
    if (item.key === 'affiliates') return canAccessAffiliates
    if (item.key === 'indicator') return canAccessIndicatorPortal
    if (item.key === 'business') return canAccessBusiness
    if (item.key === 'benefits' && isSellerAccount) return false
    if (
      item.key === 'marketplace'
      && (
        !canAccessMarketplace
        || (
          !accountProfile?.buyerUser?.acessoMarketplaceAnuncios
          && !accountProfile?.buyerUser?.acessoMarketplacePerguntas
        )
      )
    ) return false
    if (
      (item.key === 'orders' && !accountProfile?.buyerUser?.acessoPedidos)
      || (item.key === 'finance' && !accountProfile?.buyerUser?.acessoFinanceiro)
      || (item.key === 'returns' && !accountProfile?.buyerUser?.acessoPedidos)
    ) {
      return false
    }
    return true
  })

  const shortcutAccountItems = accountMenuItems.filter((item) =>
    [
      'dashboard',
      'orders',
      'quotes',
      'marketplace',
      'benefits',
      'business',
    ].includes(item.key)
  )
  const accountNotificationCount = visibleAccountNotifications.length
  const accountNotificationTone = visibleAccountNotifications[0]?.tone ?? 'neutral'
  const accountTriggerAccentColor = isHexColor(secondaryColor) ? secondaryColor : '#ff9b00'
  const accountTriggerContrastColor = isHexColor(primaryColor) ? resolveContrastColor(primaryColor) : '#111827'
  const shouldUseHighContrastAccountTriggerIcon = accountNotificationTone === 'neutral'
    && isHexColor(primaryColor)
    && isHexColor(secondaryColor)
    && getContrastRatio(secondaryColor, primaryColor) < 2.2
  const searchDropdownTopClassName = isMobile ? 'top-24' : 'top-20'
  const mobileAccountTriggerClassName = cn(
    'relative h-10 w-10 rounded-full border p-0 text-header-foreground',
    accountNotificationTone === 'success'
      ? 'border-emerald-300/35 bg-emerald-400/10 hover:bg-emerald-400/16 hover:text-header-foreground data-[state=open]:border-emerald-200/60 data-[state=open]:bg-emerald-400/18'
      : accountNotificationTone === 'danger'
        ? 'border-rose-300/35 bg-rose-400/10 hover:bg-rose-400/16 hover:text-header-foreground data-[state=open]:border-rose-200/60 data-[state=open]:bg-rose-400/18'
        : accountNotificationTone === 'warning'
          ? 'border-amber-300/40 bg-amber-400/10 hover:bg-amber-400/16 hover:text-header-foreground data-[state=open]:border-amber-200/60 data-[state=open]:bg-amber-400/18'
          : 'border-white/10 bg-white/[0.04] hover:bg-white/10 hover:text-header-foreground data-[state=open]:border-white/20 data-[state=open]:bg-white/[0.08]'
  )
  const mobileAccountTriggerBadgeClassName = cn(
    'absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-[#161616] px-1 text-[10px] font-semibold leading-none text-white',
    accountNotificationTone === 'success'
      ? 'bg-emerald-500'
      : accountNotificationTone === 'danger'
        ? 'bg-rose-500'
        : accountNotificationTone === 'warning'
          ? 'bg-amber-500'
          : 'bg-[#FF9B00]'
  )
  const accountTriggerClassName = cn(
    'rounded-full border font-medium text-header-foreground data-[state=open]:[&_svg.account-caret]:rotate-180',
    isMobile ? 'h-11 gap-2 px-3' : 'h-12 gap-3 px-3.5',
    accountNotificationTone === 'success'
      ? 'border-emerald-300/35 bg-emerald-400/10 hover:bg-emerald-400/16 hover:text-header-foreground data-[state=open]:border-emerald-200/60 data-[state=open]:bg-emerald-400/18'
      : accountNotificationTone === 'danger'
        ? 'border-rose-300/35 bg-rose-400/10 hover:bg-rose-400/16 hover:text-header-foreground data-[state=open]:border-rose-200/60 data-[state=open]:bg-rose-400/18'
        : accountNotificationTone === 'warning'
          ? 'border-amber-300/40 bg-amber-400/10 hover:bg-amber-400/16 hover:text-header-foreground data-[state=open]:border-amber-200/60 data-[state=open]:bg-amber-400/18'
          : 'border-white/10 bg-white/[0.04] hover:bg-white/10 hover:text-header-foreground data-[state=open]:border-white/20 data-[state=open]:bg-white/[0.08]'
  )
  const accountTriggerBellClassName = cn(
    'relative flex items-center justify-center rounded-full',
    isMobile ? 'h-7 w-7' : 'h-8 w-8',
    accountNotificationTone === 'success'
      ? 'bg-emerald-400/18 text-emerald-100 ring-1 ring-emerald-200/20'
      : accountNotificationTone === 'danger'
        ? 'bg-rose-400/18 text-rose-100 ring-1 ring-rose-200/20'
        : accountNotificationTone === 'warning'
          ? 'bg-amber-400/18 text-amber-100 ring-1 ring-amber-200/25'
          : ''
  )
  const accountTriggerBellStyle = accountNotificationTone === 'neutral'
    ? shouldUseHighContrastAccountTriggerIcon
      ? {
          backgroundColor: hexToRgba(accountTriggerContrastColor, 0.12),
          boxShadow: `inset 0 0 0 1px ${hexToRgba(accountTriggerContrastColor, 0.2)}`,
          color: accountTriggerContrastColor,
        }
      : {
          backgroundColor: hexToRgba(accountTriggerAccentColor, 0.16),
          boxShadow: `inset 0 0 0 1px ${hexToRgba(accountTriggerAccentColor, 0.22)}`,
          color: accountTriggerAccentColor,
        }
    : undefined
  const accountTriggerBadgeClassName = cn(
    'absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-[#161616] px-1 text-[10px] font-semibold leading-none text-white',
    accountNotificationTone === 'success'
      ? 'bg-emerald-500'
      : accountNotificationTone === 'danger'
        ? 'bg-rose-500'
        : accountNotificationTone === 'warning'
          ? 'bg-amber-500'
          : 'bg-[#FF9B00]'
  )

  const renderAccountMenuItem = (
    item: (typeof accountMenuItems)[number]
  ) => {
    const Icon = item.icon
    const itemClassName = cn(
      accountMenuItemClassName,
      item.active ? 'bg-[#FFF7ED] text-slate-950' : ''
    )
    const iconClassName = cn(
      accountMenuIconClassName,
      item.active ? 'bg-[#FFF4E5] text-[#FF9B00]' : ''
    )
    const iconWeight = item.key === 'benefits' ? 'fill' : 'bold'
    const content = (
      <>
        <span data-role="icon" className={iconClassName}>
          <Icon className="h-[18px] w-[18px] text-current" weight={iconWeight} />
        </span>
        <div className="min-w-0 flex-1">
          <span className="block truncate whitespace-nowrap text-[13px] font-semibold leading-tight">
            {item.title}
          </span>
        </div>
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors group-data-[highlighted]:text-[#FF9B00]">
          <CaretRight className="h-3.5 w-3.5 text-current transition-transform group-data-[highlighted]:translate-x-[1px]" />
        </span>
      </>
    )

    if (item.href && item.view) {
      return (
        <DropdownMenuItem
          key={item.key}
          asChild
          className={itemClassName}
        >
          <a
            href={item.href}
            onClick={(event) => handleLinkClick(event, () => {
              closeAccountMenu()
              onViewChange(item.view)
            })}
          >
            {content}
          </a>
        </DropdownMenuItem>
      )
    }

    return (
      <DropdownMenuItem
        key={item.key}
        className={itemClassName}
        onClick={() => {
          closeAccountMenu()
          item.onClick?.()
        }}
      >
        {content}
      </DropdownMenuItem>
    )
  }

  const renderAccountNotification = (
    notification: (typeof visibleAccountNotifications)[number]
  ) => {
    const cardClassName = notification.tone === 'success'
      ? 'border-emerald-200 bg-[linear-gradient(180deg,#ECFDF5_0%,#FFFFFF_100%)]'
      : notification.tone === 'danger'
        ? 'border-rose-200 bg-[linear-gradient(180deg,#FFF1F2_0%,#FFFFFF_100%)]'
        : notification.tone === 'warning'
          ? 'border-amber-200 bg-[linear-gradient(180deg,#FFF7ED_0%,#FFFFFF_100%)]'
          : 'border-slate-200 bg-white'
    const badgeClassName = notification.tone === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : notification.tone === 'danger'
        ? 'border-rose-200 bg-rose-50 text-rose-700'
        : notification.tone === 'warning'
          ? 'border-amber-200 bg-amber-50 text-amber-700'
          : 'border-slate-200 bg-slate-100 text-slate-700'
    const titleClassName = notification.tone === 'success'
      ? 'text-emerald-700'
      : notification.tone === 'danger'
        ? 'text-rose-700'
        : notification.tone === 'warning'
          ? 'text-amber-700'
          : 'text-slate-900'

    const content = (
      <div className="flex min-w-0 items-center gap-2">
        <span
          className={cn(
            'inline-flex w-fit max-w-full shrink-0 truncate rounded-full border px-2 py-0 text-[9px] font-semibold uppercase leading-4 tracking-[0.14em]',
            badgeClassName
          )}
        >
          {notification.statusLabel}
        </span>
        <p className={cn('min-w-0 flex-1 truncate text-[13px] font-semibold leading-4', titleClassName)}>
          {notification.title}
        </p>
      </div>
    )

    if (notification.href && notification.view) {
      return (
        <DropdownMenuItem
          key={notification.key}
          asChild
          className="cursor-pointer rounded-[18px] p-0 outline-none focus:bg-transparent data-[highlighted]:bg-transparent"
        >
          <a
            href={notification.href}
            onClick={(event) => handleLinkClick(event, () => {
              closeAccountMenu()
              onViewChange(notification.view!)
            })}
            className={cn(
              'block cursor-pointer rounded-[14px] border px-3 py-2 transition-all duration-200',
              cardClassName,
              'hover:-translate-y-px hover:border-[#FF9B00]/35 hover:bg-[#FFF8F0] hover:shadow-[0_12px_24px_-18px_rgba(15,23,42,0.35)]'
            )}
          >
            {content}
          </a>
        </DropdownMenuItem>
      )
    }

    return (
      <div
        key={notification.key}
        className={cn('rounded-[14px] border px-3 py-2', cardClassName)}
      >
        {content}
      </div>
    )
  }

  const renderAccountSheetItem = (
    item: (typeof accountMenuItems)[number]
  ) => {
    const Icon = item.icon
    const iconWeight = item.key === 'benefits' ? 'fill' : 'bold'
    const className = cn(
      'group flex w-full items-center gap-3 rounded-[18px] border px-4 py-3 text-left transition-colors',
      item.active
        ? 'border-[#FF9B00]/25 bg-[#FFF8F0] text-slate-950'
        : 'border-slate-200/80 bg-white text-slate-800 hover:border-[#FF9B00]/20 hover:bg-[#FFF8F0]'
    )
    const content = (
      <>
        <span
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] transition-colors',
            item.active ? 'bg-[#FFF4E5] text-[#FF9B00]' : 'bg-slate-50 text-slate-500'
          )}
        >
          <Icon className="h-[18px] w-[18px] text-current" weight={iconWeight} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold leading-tight">{item.title}</p>
        </div>
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors group-hover:text-[#FF9B00]">
          <CaretRight className="h-4 w-4" />
        </span>
      </>
    )

    if (item.href && item.view) {
      return (
        <a
          key={`mobile-account-${item.key}`}
          href={item.href}
          className={className}
          onClick={(event) => handleLinkClick(event, () => {
            closeAccountMenu()
            onViewChange(item.view)
          })}
        >
          {content}
        </a>
      )
    }

    return (
      <button
        key={`mobile-account-${item.key}`}
        type="button"
        className={className}
        onClick={() => {
          closeAccountMenu()
          item.onClick?.()
        }}
      >
        {content}
      </button>
    )
  }

  const renderAccountNotificationSheetCard = (
    notification: (typeof visibleAccountNotifications)[number]
  ) => {
    const cardClassName = notification.tone === 'success'
      ? 'border-emerald-200 bg-[linear-gradient(180deg,#ECFDF5_0%,#FFFFFF_100%)]'
      : notification.tone === 'danger'
        ? 'border-rose-200 bg-[linear-gradient(180deg,#FFF1F2_0%,#FFFFFF_100%)]'
        : notification.tone === 'warning'
          ? 'border-amber-200 bg-[linear-gradient(180deg,#FFF7ED_0%,#FFFFFF_100%)]'
          : 'border-slate-200 bg-white'
    const badgeClassName = notification.tone === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : notification.tone === 'danger'
        ? 'border-rose-200 bg-rose-50 text-rose-700'
        : notification.tone === 'warning'
          ? 'border-amber-200 bg-amber-50 text-amber-700'
          : 'border-slate-200 bg-slate-100 text-slate-700'
    const titleClassName = notification.tone === 'success'
      ? 'text-emerald-700'
      : notification.tone === 'danger'
        ? 'text-rose-700'
        : notification.tone === 'warning'
          ? 'text-amber-700'
          : 'text-slate-900'

    const content = (
      <div className="flex min-w-0 items-center gap-2">
        <span
          className={cn(
            'inline-flex w-fit max-w-full shrink-0 truncate rounded-full border px-2 py-0 text-[9px] font-semibold uppercase leading-4 tracking-[0.14em]',
            badgeClassName
          )}
        >
          {notification.statusLabel}
        </span>
        <p className={cn('min-w-0 flex-1 truncate text-[13px] font-semibold leading-4', titleClassName)}>
          {notification.title}
        </p>
      </div>
    )

    if (notification.href && notification.view) {
      return (
        <a
          key={`mobile-notification-${notification.key}`}
          href={notification.href}
          className={cn(
            'block rounded-[14px] border px-3 py-2 transition-colors',
            cardClassName
          )}
          onClick={(event) => handleLinkClick(event, () => {
            closeAccountMenu()
            onViewChange(notification.view!)
          })}
        >
          {content}
        </a>
      )
    }

    return (
      <div
        key={`mobile-notification-${notification.key}`}
        className={cn('rounded-[14px] border px-3 py-2', cardClassName)}
      >
        {content}
      </div>
    )
  }

  const addToSearchHistory = useCallback((query: string) => {
    const trimmedQuery = query.trim()
    if (!trimmedQuery || trimmedQuery.length < 2) return

    setSearchHistory((current) => {
      const existing = current || []
      const filtered = existing.filter(q => q.toLowerCase() !== trimmedQuery.toLowerCase())
      return [trimmedQuery, ...filtered].slice(0, 10)
    })
  }, [setSearchHistory])

  const handleVoiceTranscript = useCallback((transcript: string) => {
    setSearchQuery(transcript)
    onSearchChange(transcript)
    addToSearchHistory(transcript)
    toast.success(t('toasts.voice_captured'), {
      description: transcript,
    })
  }, [addToSearchHistory, onSearchChange, t])

  const { isListening, isSupported, toggleListening } = useVoiceSearch(handleVoiceTranscript)
  
  const {
    suggestions: autocompleteSuggestions,
    isLoading: isLoadingSuggestions,
    isLoadingMore: isLoadingMoreSuggestions,
    hasMore: hasMoreAutocompleteSuggestions,
    loadMore: loadMoreAutocompleteSuggestions,
  } = useSearchAutocomplete({
    query: searchQuery,
    minQueryLength: 2,
    debounceMs: 300,
    enabled: showSearchDropdown && !showEnhancedDropdown,
  })
  const normalizedSearchQuery = searchQuery.trim()
  const showMobileSearchResultsOnly = isMobile && normalizedSearchQuery.length >= 2

  useEffect(() => {
    const target = autocompleteLoadMoreRef.current
    if (
      !target
      || !showSearchDropdown
      || showEnhancedDropdown
      || isLoadingSuggestions
      || isLoadingMoreSuggestions
      || !hasMoreAutocompleteSuggestions
    ) return

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        void loadMoreAutocompleteSuggestions()
      }
    }, {
      threshold: 0.01,
      rootMargin: '160px 0px',
    })

    observer.observe(target)
    return () => observer.disconnect()
  }, [
    autocompleteSuggestions.length,
    hasMoreAutocompleteSuggestions,
    isLoadingMoreSuggestions,
    isLoadingSuggestions,
    loadMoreAutocompleteSuggestions,
    showEnhancedDropdown,
    showSearchDropdown,
  ])

  useEffect(() => {
    if (!isMobile || (!showSearchDropdown && !showEnhancedDropdown) || isListening) {
      setMobileSearchPanelFrame(null)
      return
    }

    const updatePanelFrame = () => {
      if (!searchRef.current) return
      const rect = searchRef.current.getBoundingClientRect()
      setMobileSearchPanelFrame({
        top: Math.round(rect.bottom + 8),
        left: Math.round(rect.left),
        width: Math.round(rect.width),
      })
    }

    updatePanelFrame()

    window.addEventListener('resize', updatePanelFrame)
    window.addEventListener('scroll', updatePanelFrame, true)

    return () => {
      window.removeEventListener('resize', updatePanelFrame)
      window.removeEventListener('scroll', updatePanelFrame, true)
    }
  }, [isListening, isMobile, showEnhancedDropdown, showSearchDropdown])

  const removeFromSearchHistory = (query: string) => {
    setSearchHistory((current) => {
      const existing = current || []
      return existing.filter(q => q !== query)
    })
  }

  const clearSearchHistory = () => {
    setSearchHistory([])
    toast.info(t('toasts.search_history_cleared'))
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        closeSearchDropdown()
        setIsAiSearchOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [closeSearchDropdown])

  const handleSearchFocus = () => {
    closeMegaMenus()
    if (searchQuery.trim()) {
      setShowSearchDropdown(true)
      setShowEnhancedDropdown(false)
      return
    }

    setShowSearchDropdown(false)
    setShowEnhancedDropdown(true)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    closeMegaMenus()
    setSearchQuery(value)
    
    if (value.trim()) {
      setShowEnhancedDropdown(false)
      setShowSearchDropdown(true)
    } else {
      setShowSearchDropdown(false)
      setShowEnhancedDropdown(true)
    }
  }

  const handleSearchSubmit = (query?: string) => {
    const explicitQuery = typeof query === 'string' ? query.trim() : ''
    const inputQuery = searchInputRef.current?.value?.trim() || ''
    const stateQuery = searchQuery.trim()
    const rawQuery = explicitQuery || inputQuery || stateQuery
    const normalizedQuery = rawQuery.trim()
    closeMegaMenus()

    if (normalizedQuery) {
      addToSearchHistory(normalizedQuery)
    }

    setSearchQuery(normalizedQuery)
    onSearchChange(normalizedQuery)
    closeSearchDropdown()
  }

  const handleImageSearchButtonClick = () => {
    if (isImageSearchLoading) return
    imageInputRef.current?.click()
  }

  const handleImageInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.currentTarget.value = ''
    if (!file) return

    const mimeType = file.type.trim().toLowerCase()
    if (!IMAGE_SEARCH_ALLOWED_FILE_TYPES.has(mimeType)) {
      toast.error(t('header.search.image_invalid_type'))
      return
    }

    if (file.size <= 0 || file.size > IMAGE_SEARCH_MAX_FILE_BYTES) {
      toast.error(t('header.search.image_invalid_size'))
      return
    }

    closeMegaMenus()
    closeSearchDropdown()

    try {
      await onImageSearch?.(file)
    } catch {
      // A tela principal trata a mensagem de erro.
    }
  }

  const handleCategorySelect = (categoryId: string, categoryData?: CategorySelectionData) => {
    onCategorySelect(categoryId, categoryData)
    setShowMegaMenu(false)
    setShowMobileCategoryMenu(false)
  }

  const handlePathNavigation = (href: string) => {
    if (typeof window === 'undefined') return

    const targetUrl = new URL(href, window.location.origin)
    const nextPath = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`

    const scrollToHashTarget = () => {
      if (!targetUrl.hash) return
      const targetId = decodeURIComponent(targetUrl.hash.slice(1))
      const targetElement = document.getElementById(targetId)
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }

    if (currentPath !== nextPath) {
      window.history.pushState({}, '', nextPath)
      window.dispatchEvent(new PopStateEvent('popstate'))
    }

    if (targetUrl.hash) {
      window.requestAnimationFrame(scrollToHashTarget)
    } else if (currentPath === nextPath) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    }
  }

  const storeQuickLinks = [
    {
      key: 'launches',
      icon: Sparkle,
      href: VIEW_PATHS.launches,
      isActive: currentView === 'launches',
      onClick: () => {
        closeSearchDropdown()
        closeMegaMenus()
        onViewChange('launches')
      },
    },
    {
      key: 'just_arrived',
      icon: Clock,
      href: VIEW_PATHS.just_arrived,
      isActive: currentView === 'just_arrived',
      onClick: () => {
        closeSearchDropdown()
        closeMegaMenus()
        onViewChange('just_arrived')
      },
    },
    {
      key: 'contact',
      icon: Headset,
      href: VIEW_PATHS.support,
      isActive: currentView === 'support',
      onClick: () => {
        closeSearchDropdown()
        closeMegaMenus()
        onViewChange('support')
      },
    },
    {
      key: 'tracking',
      icon: Truck,
      href: VIEW_PATHS.tracking,
      isActive: currentView === 'tracking',
      onClick: () => {
        closeSearchDropdown()
        closeMegaMenus()
        onViewChange('tracking')
      },
    },
    {
      key: 'price_table',
      icon: isLoggedIn ? CurrencyCircleDollar : Package,
      label: isLoggedIn
        ? t('header.quick_links.price_table')
        : t('account.priceTable.guest_title'),
      href: priceTableHref,
      isActive: currentView === 'price-table',
      openInNewTab: true,
      onClick: () => {
        closeSearchDropdown()
        closeMegaMenus()
      },
    },
    ...(storeNetworkMarketplaceEnabled ? [{
      key: 'store_network_marketplace',
      icon: Storefront,
      label: 'Marketplace',
      href: VIEW_PATHS['store-network-marketplace'],
      isActive: currentView === 'store-network-marketplace',
      onClick: () => {
        closeSearchDropdown()
        closeMegaMenus()
        queueStorefrontAnalyticsEvent({
          eventType: 'important_click',
          eventKey: 'menu_click',
          pageKind: 'other',
          pageKey: 'store_network_marketplace',
          payload: {
            scope: 'store_network',
            journey_id: getStoreNetworkJourneyId(),
            origin_global_store_id: storeNetworkOriginGlobalStoreId || null,
            target_global_store_id: null,
          },
        })
        onViewChange('store-network-marketplace')
      },
    }] : []),
  ] as const

  const mobileCategoriesQuickLink = {
    key: 'categories',
    label: t('header.categories'),
    icon: List,
    href: VIEW_PATHS.products,
    isActive: false,
    onClick: () => {
      closeSearchDropdown()
      onCategoriesPrefetch?.()
      setShowMegaMenu(false)
      setShowMobileCategoryMenu(true)
    },
  } as const

  const baseQuickLinkItems = useMemo<HeaderQuickLinkItem[]>(() => {
    const items: HeaderQuickLinkItem[] = []

    if (!isMobile && currentView !== 'blog') {
      items.push({
        key: mobileCategoriesQuickLink.key,
        kind: 'categories',
        icon: mobileCategoriesQuickLink.icon,
        href: mobileCategoriesQuickLink.href,
        label: mobileCategoriesQuickLink.label,
        isActive: mobileCategoriesQuickLink.isActive,
        onClick: mobileCategoriesQuickLink.onClick,
        itemClassName: 'hidden max-[1360px]:block',
      })
    }

    items.push(
      ...storeQuickLinks.map((item) => ({
        key: item.key,
        kind: 'default' as const,
        icon: item.icon,
        href: item.href,
        label: 'label' in item ? item.label : t(`header.quick_links.${item.key}`),
        isActive: item.isActive,
        onClick: item.onClick,
        openInNewTab: 'openInNewTab' in item ? item.openInNewTab : undefined,
      }))
    )

    return items
  }, [currentView, isMobile, mobileCategoriesQuickLink, storeQuickLinks, t])

  const moreOptionsLabel = t('header.quick_links.more_options')
  const renderedBaseQuickLinkItems = useMemo(() => {
    if (renderedBaseQuickLinkKeys === null) return baseQuickLinkItems
    const renderedKeys = new Set(renderedBaseQuickLinkKeys)
    return baseQuickLinkItems.filter((item) => renderedKeys.has(item.key))
  }, [baseQuickLinkItems, renderedBaseQuickLinkKeys])

  const visibleBaseQuickLinkItems = useMemo(() => {
    if (visibleBaseQuickLinkKeys === null) return renderedBaseQuickLinkItems
    const visibleKeys = new Set(visibleBaseQuickLinkKeys)
    return renderedBaseQuickLinkItems.filter((item) => visibleKeys.has(item.key))
  }, [renderedBaseQuickLinkItems, visibleBaseQuickLinkKeys])

  const overflowBaseQuickLinkItems = useMemo(() => {
    if (visibleBaseQuickLinkKeys === null) return [] as HeaderQuickLinkItem[]
    const visibleKeys = new Set(visibleBaseQuickLinkKeys)
    return renderedBaseQuickLinkItems.filter((item) => !visibleKeys.has(item.key))
  }, [renderedBaseQuickLinkItems, visibleBaseQuickLinkKeys])

  const hasActiveOverflowQuickLink = overflowBaseQuickLinkItems.some((item) => item.isActive)

  const quickLinkButtonBaseClassName = 'inline-flex h-12 items-center gap-2 border-b-2 px-0 text-sm font-medium whitespace-nowrap transition-colors focus-visible:outline-none'
  const quickLinkInactiveClassName = 'border-transparent text-foreground/75 hover:border-border hover:text-foreground focus-visible:border-accent focus-visible:text-foreground'
  const quickLinkActiveClassName = 'border-accent text-foreground'
  const moreOptionsTriggerClassName = cn(
    quickLinkButtonBaseClassName,
    hasActiveOverflowQuickLink ? quickLinkActiveClassName : quickLinkInactiveClassName
  )

  const renderBaseQuickLink = (item: HeaderQuickLinkItem) => {
    const Icon = item.icon
    const anchorClassName = item.kind === 'categories'
      ? 'inline-flex h-12 items-center gap-2 border-b-2 border-transparent px-0 text-sm font-medium whitespace-nowrap text-foreground/75 transition-colors hover:border-border hover:text-foreground focus-visible:border-accent focus-visible:text-foreground focus-visible:outline-none max-[1200px]:gap-0'
      : cn(
          quickLinkButtonBaseClassName,
          item.isActive ? quickLinkActiveClassName : quickLinkInactiveClassName
        )

    return (
      <a
        href={item.href}
        target={item.openInNewTab ? '_blank' : undefined}
        rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
        onClick={item.openInNewTab
          ? () => item.onClick()
          : (event) => handleLinkClick(event, item.onClick)}
        aria-current={item.isActive ? 'page' : undefined}
        className={anchorClassName}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className={item.kind === 'categories' ? 'max-[1200px]:hidden' : undefined}>
          {item.label}
        </span>
      </a>
    )
  }

  const renderOverflowQuickLink = (item: HeaderQuickLinkItem) => {
    const Icon = item.icon
    const itemClassName = cn(
      'cursor-pointer rounded-[12px] px-3 py-2 text-sm font-medium text-slate-700 outline-none data-[highlighted]:bg-[#FFF8F0] data-[highlighted]:text-slate-950',
      item.isActive && 'bg-[#FFF8F0] text-slate-950'
    )

    return (
      <DropdownMenuItem key={`overflow-${item.key}`} asChild className={itemClassName}>
        <a
          href={item.href}
          target={item.openInNewTab ? '_blank' : undefined}
          rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
          onClick={item.openInNewTab
            ? () => item.onClick()
            : (event) => handleLinkClick(event, item.onClick)}
          aria-current={item.isActive ? 'page' : undefined}
        >
          <Icon className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
        </a>
      </DropdownMenuItem>
    )
  }

  useEffect(() => {
    const layout = quickLinksLayoutRef.current
    if (!layout) return

    let frameId = 0
    let resizeObserver: ResizeObserver | null = null

    const scheduleMeasure = () => {
      if (frameId) window.cancelAnimationFrame(frameId)
      frameId = window.requestAnimationFrame(() => {
        const layoutNode = quickLinksLayoutRef.current
        if (!layoutNode) return

        const measuredItems = baseQuickLinkItems
          .map((item) => ({
            key: item.key,
            width: quickLinkMeasureItemRefs.current[item.key]?.offsetWidth ?? 0,
          }))
          .filter((item) => item.width > 0)
        const measuredKeys = measuredItems.map((item) => item.key)

        if (measuredItems.length === 0) {
          setRenderedBaseQuickLinkKeys((current) => (areStringArraysEqual(current, []) ? current : []))
          setVisibleBaseQuickLinkKeys((current) => (areStringArraysEqual(current, []) ? current : []))
          return
        }

        setRenderedBaseQuickLinkKeys((current) => (areStringArraysEqual(current, measuredKeys) ? current : measuredKeys))

        const styles = window.getComputedStyle(layoutNode)
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0
        const vehicleWidth = showVehiclePlateFilter ? (vehicleQuickLinkRef.current?.offsetWidth ?? 0) : 0
        const appsWidth = appsQuickLinkRef.current?.offsetWidth ?? 0
        const rightItemsCount = Number(vehicleWidth > 0) + Number(appsWidth > 0)
        const availableBaseWidth = Math.max(0, layoutNode.clientWidth - vehicleWidth - appsWidth - (gap * rightItemsCount))
        const moreOptionsWidth = moreOptionsMeasureRef.current?.offsetWidth ?? 0
        const totalItemsWidth = measuredItems.reduce((sum, item) => sum + item.width, 0) + (gap * Math.max(0, measuredItems.length - 1))

        if (totalItemsWidth <= availableBaseWidth) {
          const nextKeys = measuredKeys
          setVisibleBaseQuickLinkKeys((current) => (areStringArraysEqual(current, nextKeys) ? current : nextKeys))
          return
        }

        const nextKeys: string[] = []
        let usedWidth = 0

        for (let index = 0; index < measuredItems.length; index += 1) {
          const item = measuredItems[index]
          const nextItemWidth = item.width + (nextKeys.length > 0 ? gap : 0)
          const hasRemainingItems = index < measuredItems.length - 1
          const overflowReserveWidth = hasRemainingItems
            ? moreOptionsWidth + (nextKeys.length > 0 ? gap : 0)
            : 0

          if (usedWidth + nextItemWidth + overflowReserveWidth > availableBaseWidth) {
            break
          }

          nextKeys.push(item.key)
          usedWidth += nextItemWidth
        }

        setVisibleBaseQuickLinkKeys((current) => (areStringArraysEqual(current, nextKeys) ? current : nextKeys))
      })
    }

    scheduleMeasure()
    window.addEventListener('resize', scheduleMeasure)

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleMeasure)
      resizeObserver.observe(layout)
      if (quickLinksMeasureListRef.current) resizeObserver.observe(quickLinksMeasureListRef.current)
      if (vehicleQuickLinkRef.current) resizeObserver.observe(vehicleQuickLinkRef.current)
      if (appsQuickLinkRef.current) resizeObserver.observe(appsQuickLinkRef.current)
      if (moreOptionsMeasureRef.current) resizeObserver.observe(moreOptionsMeasureRef.current)
    }

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId)
      window.removeEventListener('resize', scheduleMeasure)
      resizeObserver?.disconnect()
    }
  }, [baseQuickLinkItems, showAppsMenu, showCommunityLinks, showTechnicalTablesLink, showVehiclePlateFilter])

  const handleHeaderVehicleSearch = async (params: { query: string; vehicle: VehicleFilter }) => {
    closeSearchDropdown()
    closeMegaMenus()
    addToSearchHistory(params.query)

    if (onVehicleSearch) {
      await onVehicleSearch(params)
      return
    }

    onSearchChange(params.query)
  }

  const appMenuSegments = [
    { key: 'featured' },
    { key: 'training' },
    { key: 'engineering' },
    { key: 'sales' },
  ] as const

  const appLinks = menuAppsForVisitor(isLoggedIn && accountProfile?.electricStudioEnabled === true)
    .map((item) => ({
      ...item,
      href: item.key === 'university' && isLoggedIn
        ? '/api/apps/universidade/acesso'
        : item.key === 'consultaeng' && isLoggedIn
          ? '/api/apps/consultaeng/acesso'
          : item.key === 'blog'
            ? VIEW_PATHS.community
            : item.href,
      internalView: item.key === 'blog' ? 'community' as const : item.internalView,
      description: t(`header.quick_links.apps_menu.${item.key}_description`),
    }))

  const appMenuPanels = appMenuSegments.map((segment) => ({
    key: segment.key,
    appLinks: appLinks.filter((item) => item.segmentKeys.includes(segment.key)),
  }))
  const normalizedAppSearch = appSearchQuery.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  const isAppSearchActive = normalizedAppSearch.length > 0
  const activeAppLinks = isAppSearchActive
    ? appLinks.filter((item) => `${t(`header.quick_links.${item.key}`)} ${item.description}`
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(normalizedAppSearch))
    : (appMenuPanels.find((panel) => panel.key === activeAppSegment)?.appLinks ?? [])

  const selectAppSegment = (segment: AppSegmentKey) => {
    setActiveAppSegment(segment)
    setAppSearchQuery('')
  }

  const renderAppSearchInput = (id: string, className?: string) => (
    <div className={cn('relative', className)}>
      <MagnifyingGlass aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        id={id}
        type="text"
        inputMode="search"
        autoComplete="off"
        spellCheck={false}
        value={appSearchQuery}
        onChange={(event) => setAppSearchQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') event.stopPropagation()
        }}
        aria-label={t('header.quick_links.apps_menu.search_placeholder')}
        placeholder={t('header.quick_links.apps_menu.search_placeholder')}
        className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-9 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-orange-300 focus:bg-white focus:ring-2 focus:ring-orange-100"
      />
      {appSearchQuery ? (
        <button
          type="button"
          aria-label={t('header.quick_links.apps_menu.search_clear')}
          onClick={() => setAppSearchQuery('')}
          className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  )

  const renderMobileAppLink = (item: (typeof appLinks)[number]) => {
    const content = (
      <>
        <span
          aria-hidden="true"
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-[12px] text-base font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]',
            item.tileClassName,
          )}
        >
          {item.imageSrc ? (
            <img
              src={item.imageSrc}
              alt={t(`header.quick_links.${item.key}`)}
              className={cn(
                'max-h-full max-w-full',
                item.imageClassName ?? 'h-full w-full object-cover'
              )}
            />
          ) : (
            item.monogram
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold text-slate-950">
            {t(`header.quick_links.${item.key}`)}
          </p>
          <p className="mt-0.5 text-[13px] leading-5 text-slate-500">
            {item.description}
          </p>
        </div>
        <span className="inline-flex shrink-0 items-center justify-center rounded-full text-slate-400">
          <CaretRight className="h-4 w-4" />
        </span>
      </>
    )

    if (item.internalView && !item.openInNewTab) {
      return (
        <a
          key={`mobile-app-${item.key}`}
          href={item.href}
          className={cn(
            'flex items-center gap-3 rounded-[18px] border px-4 py-3 text-left',
            item.cardClassName,
          )}
          onClick={(event) => handleLinkClick(event, () => {
            closeAppsMenu()
            closeSearchDropdown()
            closeMegaMenus()
            onViewChange(item.internalView!)
          })}
        >
          {content}
        </a>
      )
    }

    return (
      <a
        key={`mobile-app-${item.key}`}
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'flex items-center gap-3 rounded-[18px] border px-4 py-3 text-left',
          item.cardClassName,
        )}
        onClick={() => {
          closeAppsMenu()
          closeSearchDropdown()
          closeMegaMenus()
        }}
      >
        {content}
      </a>
    )
  }

  const renderDesktopAppLink = (item: (typeof appLinks)[number], isFeatured: boolean) => (
    <DropdownMenuItem
      key={item.key}
      asChild
      className={cn(
        'group rounded-[18px] border p-0 outline-none transition-all duration-200 focus:text-slate-950 data-[highlighted]:border-orange-300 data-[highlighted]:text-slate-950 data-[highlighted]:shadow-[0_10px_25px_rgba(15,23,42,0.08)]',
        isFeatured
          ? 'col-span-2 border-orange-200 bg-[#FFFBF6] shadow-[0_10px_30px_rgba(180,83,9,0.06)] hover:border-orange-300 hover:bg-[#FFF8F0] focus:bg-[#FFF8F0] data-[highlighted]:bg-[#FFF8F0]'
          : 'border-slate-200 bg-white shadow-[0_3px_12px_rgba(15,23,42,0.03)] hover:border-orange-200 hover:bg-orange-50/40 focus:bg-orange-50/40 data-[highlighted]:bg-orange-50/40',
      )}
    >
      <a
        href={item.href}
        target={item.internalView && !item.openInNewTab ? undefined : '_blank'}
        rel={item.internalView && !item.openInNewTab ? undefined : 'noopener noreferrer'}
        onClick={(event) => {
          if (item.internalView && !item.openInNewTab) {
            handleLinkClick(event, () => {
              closeSearchDropdown()
              closeMegaMenus()
              onViewChange(item.internalView!)
            })
            return
          }
          closeSearchDropdown()
          closeMegaMenus()
        }}
        className={cn('flex h-full items-center gap-3.5 text-left', isFeatured ? 'px-5 py-4' : 'px-4 py-4')}
      >
        <span
          aria-hidden="true"
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-[13px] text-base font-semibold transition-transform duration-200 group-hover:-translate-y-0.5',
            item.tileClassName,
          )}
        >
          {item.imageSrc ? (
            <img
              src={item.imageSrc}
              alt={t(`header.quick_links.${item.key}`)}
              width={48}
              height={48}
              loading="lazy"
              decoding="async"
              className={cn('max-h-full max-w-full', item.imageClassName ?? 'h-full w-full object-cover')}
            />
          ) : (
            item.monogram
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold leading-5 text-slate-950">
            {t(`header.quick_links.${item.key}`)}
          </span>
          <span className="mt-1 block text-xs leading-[1.45] text-slate-500">
            {item.description}
          </span>
        </span>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors group-hover:border-orange-300 group-hover:bg-orange-50 group-hover:text-orange-700 group-data-[highlighted]:border-orange-300 group-data-[highlighted]:bg-orange-50 group-data-[highlighted]:text-orange-700">
          <CaretRight className="h-3.5 w-3.5 text-current" />
        </span>
      </a>
    </DropdownMenuItem>
  )

  return (
    <>
    <header className="sticky top-0 z-50 border-b border-header-bg/20 bg-header-bg shadow-lg">
      <div className="container mx-auto px-4">
        <div className={cn('flex items-center', isMobile ? 'min-h-12 flex-wrap gap-2 py-2' : 'h-20 gap-6')}>
          <a
            href={VIEW_PATHS.home}
            aria-label={siteName}
            className={cn(
              'flex shrink-0 items-center',
              isMobile ? 'order-1 h-8 w-[90px]' : 'h-10 w-[109px] lg:h-12 lg:w-[131px]'
            )}
            onClick={(event) => handleLinkClick(event, () => onViewChange('home'))}
          >
            {headerLogoUrl ? (
              <img
                src={headerLogoUrl}
                alt={siteName}
                width={212}
                height={78}
                fetchPriority="high"
                decoding="async"
                draggable={false}
                className="h-full w-full object-contain"
              />
            ) : (
              <span
                aria-hidden="true"
                className="h-full w-full animate-pulse rounded-lg bg-black/5"
              />
            )}
          </a>

          {!isMobile && (
            <Button
              variant="ghost"
              className="h-11 gap-2 px-4 font-medium text-header-foreground hover:bg-white/10 hover:text-header-foreground max-[1360px]:hidden"
              aria-label={t('header.all_categories')}
              title={t('header.all_categories')}
              onClick={() => {
                closeSearchDropdown()
                onCategoriesPrefetch?.()
                setShowMegaMenu(!showMegaMenu)
              }}
            >
              <List className="h-5 w-5" />
              <span>{t('header.all_categories')}</span>
              <CaretDown className={`h-4 w-4 transition-transform ${showMegaMenu ? 'rotate-180' : ''}`} />
            </Button>
          )}

          <div className={cn('flex items-center gap-3', isMobile ? 'order-3 basis-full' : 'flex-1')}>
            <div 
              ref={searchRef} 
              className={cn('relative', isMobile ? 'w-full' : 'mx-auto flex-1 max-w-4xl')}
            >
              <div
                className={cn(
                  'relative flex items-center rounded-full bg-white shadow-lg',
                  isMobile ? 'h-11' : 'h-14'
                )}
              >
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="sr-only"
                  aria-label={t('header.search.image_button')}
                  onChange={handleImageInputChange}
                />
                {isSupported && (
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    className={`ml-2 h-10 w-10 shrink-0 transition-all rounded-full ${
                      isListening 
                        ? 'text-accent hover:text-accent/80 animate-pulse' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    onClick={toggleListening}
                    aria-label={isListening ? t('header.voice.stop') : t('header.voice.start')}
                    title={isListening ? t('header.voice.stop') : t('header.voice.start')}
                  >
                    <Microphone className="h-5 w-5" weight={isListening ? 'fill' : 'regular'} />
                  </Button>
                )}
                <div className={cn('relative flex-1 cursor-text', isMobile ? 'px-3 py-2' : 'px-4 py-3')} onClick={(e) => {
                  const input = e.currentTarget.querySelector('input')
                  if (input) input.focus()
                  handleSearchFocus()
                }}>
                  {!searchQuery && !isMobile && activeSearchHighlight ? (
                    <div
                      key={`${currentTermIndex}-${activeSearchHighlight.label}`}
                      className="pointer-events-none absolute left-4 top-1/2 hidden -translate-y-1/2 items-center gap-2 lg:flex"
                    >
                      <ActiveSearchHighlightIcon
                        className={cn(
                          'h-5 w-5 shrink-0',
                          resolveSearchHighlightIconClassName(ActiveSearchHighlightIcon)
                        )}
                        weight="fill"
                      />
                      <span className="max-w-[28rem] truncate text-base text-foreground/70">
                        {activeSearchHighlight.label}
                      </span>
                    </div>
                  ) : null}
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={handleSearchFocus}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleSearchSubmit()
                      }
                    }}
                    className={cn(
                      'relative w-full bg-transparent border-0 outline-none text-foreground text-base',
                      isMobile
                        ? 'placeholder:text-foreground/45'
                        : activeSearchHighlight
                          ? 'placeholder:text-transparent max-[1023px]:placeholder:text-foreground/45'
                          : 'placeholder:text-foreground/45'
                    )}
                    style={{ caretColor: 'auto' }}
                    placeholder={t('header.search.placeholder')}
                    aria-label={t('search.search_products')}
                  />
                </div>
                {isAiSearchAvailable ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={cn(
                      'mr-1 h-11 w-11 shrink-0 rounded-full text-muted-foreground transition-all hover:bg-muted hover:text-foreground',
                      isAiSearchOpen && 'bg-accent/10 text-accent hover:bg-accent/15 hover:text-accent'
                    )}
                    onClick={() => {
                      closeSearchDropdown()
                      closeMegaMenus()
                      setIsAiSearchOpen((prev) => !prev)
                    }}
                    aria-label={t('header.search.ai_button')}
                    title={t('header.search.ai_button')}
                  >
                    <Sparkle className="h-5 w-5" weight={isAiSearchOpen ? 'fill' : 'regular'} />
                  </Button>
                ) : null}
                <Button
                  type="button"
                  size="icon"
                  className={cn(
                    'rounded-full bg-accent text-accent-foreground shrink-0 hover:bg-accent/90',
                    isMobile ? 'h-11 w-11' : 'h-14 w-14'
                  )}
                  onClick={() => handleSearchSubmit()}
                  aria-label={t('search.search_products')}
                  title={t('search.search_products')}
                >
                  <MagnifyingGlass className="h-6 w-6" weight="bold" />
                </Button>
              </div>

              {isAiSearchAvailable ? (
                <AiSearchPanel
                  open={isAiSearchOpen}
                  seed={aiSearchSeed}
                  lojaId={aiSearchLojaId ?? 0}
                  onClose={() => setIsAiSearchOpen(false)}
                  onProductClick={(product) => {
                    setIsAiSearchOpen(false)
                    onProductClick(product)
                  }}
                  onAddToCart={(product, searchContext) => onAiAddToCart?.(product, searchContext)}
                />
              ) : null}

              {isListening && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-accent/20 rounded-lg shadow-2xl z-50 p-6 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="flex flex-col items-center gap-4">
                      <div className="flex items-center gap-3 text-accent font-semibold text-lg">
                      <div className="relative">
                        <Microphone className="h-6 w-6" weight="fill" />
                        <span className="absolute -top-1 -right-1 flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-accent"></span>
                        </span>
                      </div>
                      <span>{t('header.voice.listening')}</span>
                    </div>
                    <Suspense
                      fallback={<div className="h-20 w-full max-w-md rounded-2xl bg-accent/5" />}
                    >
                      <VoiceWaveform isListening={isListening} className="w-full max-w-md" />
                    </Suspense>
                    <p className="text-sm text-muted-foreground text-center">
                      {t('header.voice.helper')}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={toggleListening}
                      className="text-xs"
                    >
                      {t('actions.cancel')}
                    </Button>
                  </div>
                </div>
              )}

              <Suspense fallback={null}>
                <SearchDropdown
                  isMobile={isMobile}
                  open={showEnhancedDropdown && !isListening}
                  onSearchTerm={(term) => {
                    setSearchQuery(term)
                    handleSearchSubmit(term)
                  }}
                  onProductClick={onProductClick}
                  onClose={() => setShowEnhancedDropdown(false)}
                  mobilePanelFrame={mobileSearchPanelFrame}
                />
              </Suspense>

              {showSearchDropdown && !isListening && !showEnhancedDropdown && (
                <>
                  <div
                    className={cn('fixed left-0 right-0 bottom-0 z-[35] bg-black/40 backdrop-blur-sm', searchDropdownTopClassName)}
                    style={isMobile && mobileSearchPanelFrame ? { top: `${mobileSearchPanelFrame.top}px` } : undefined}
                    onClick={() => setShowSearchDropdown(false)}
                  />
                  <div className={cn(
                    'fixed z-40 overflow-y-auto overscroll-contain border bg-white shadow-2xl scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent animate-in fade-in slide-in-from-top-2 duration-200',
                    isMobile
                      ? 'inset-x-0 bottom-0 top-24 rounded-none border-x-0 border-b-0'
                      : 'left-1/2 -translate-x-1/2 rounded-lg',
                    !isMobile && searchDropdownTopClassName,
                    isMobile ? '' : 'max-h-[calc(100vh-6rem)] w-[90vw] max-w-7xl'
                  )}
                  style={isMobile && mobileSearchPanelFrame
                    ? {
                        top: `${mobileSearchPanelFrame.top}px`,
                        left: `${mobileSearchPanelFrame.left}px`,
                        width: `${mobileSearchPanelFrame.width}px`,
                      }
                    : undefined}
                  >
                    <div className={cn(showMobileSearchResultsOnly ? 'px-3' : 'px-4')}>
                      <div className={cn('grid', isMobile ? (showMobileSearchResultsOnly ? 'py-3' : 'gap-6 py-6') : 'grid-cols-12 gap-8 py-10')}>
                        {showMobileSearchResultsOnly ? (
                          <div className="min-h-0">
                            {autocompleteSuggestions.length > 0 ? (
                              <div>
                                <div className="mb-3 flex items-center justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                                      {t('header.search.results_label')}
                                    </p>
                                    <h3 className="truncate text-lg font-bold leading-5 text-foreground">
                                      “{normalizedSearchQuery}”
                                    </h3>
                                  </div>
                                  {isAiSearchAvailable ? (
                                    <button
                                      type="button"
                                      onClick={openAiSearchWithQuery}
                                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-accent/20 bg-accent/10 text-accent transition-colors hover:bg-accent hover:text-accent-foreground"
                                      aria-label={t('ai_search.use')}
                                      title={t('ai_search.use')}
                                    >
                                      <Sparkle className="h-4 w-4" />
                                    </button>
                                  ) : null}
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  {autocompleteSuggestions.map((product) => (
                                    <a
                                      key={product.id}
                                      href={buildProductHref(product)}
                                      className="group flex min-w-0 flex-col rounded-2xl border border-border/80 bg-white p-2.5 text-left transition-colors hover:border-accent hover:bg-accent/5"
                                      onClick={(event) => {
                                        handleLinkClick(event, () => {
                                          addToSearchHistory(normalizedSearchQuery)
                                          setShowSearchDropdown(false)
                                          onProductClick(product)
                                        })
                                      }}
                                    >
                                      <div className="flex h-24 w-full items-center justify-center overflow-hidden rounded-xl bg-muted/40">
                                          {product.image ? (
                                            <img
                                              src={product.image}
                                              alt={product.name}
                                              width={128}
                                              height={96}
                                              loading="lazy"
                                              decoding="async"
                                              className="h-full w-full object-contain p-2 transition-transform duration-300 group-hover:scale-105"
                                            />
                                          ) : (
                                            <Package className="h-8 w-8 text-muted-foreground" />
                                          )}
                                      </div>
                                      <h5 className="mt-2 line-clamp-2 min-h-8 text-[12px] font-semibold leading-4 text-foreground transition-colors group-hover:text-accent">
                                        {product.name}
                                      </h5>
                                      <div className="mt-1.5 flex min-w-0 items-center gap-2">
                                        <span className="min-w-0 truncate font-mono text-[9px] leading-4 text-muted-foreground">
                                          {product.code}
                                        </span>
                                        {product.brand && (
                                          <Badge variant="secondary" className="shrink-0 rounded-full px-1.5 py-0 text-[9px] leading-4">
                                            {product.brand}
                                          </Badge>
                                        )}
                                      </div>
                                      <SearchProductResultStatus product={product} compact />
                                    </a>
                                  ))}
                                </div>
                                {(hasMoreAutocompleteSuggestions || isLoadingMoreSuggestions) ? (
                                  <div
                                    ref={autocompleteLoadMoreRef}
                                    className="flex min-h-10 items-center justify-center py-3"
                                  >
                                    {isLoadingMoreSuggestions ? (
                                      <CircleNotch className="h-5 w-5 animate-spin text-accent" />
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            ) : isLoadingSuggestions ? (
                              <div className="flex items-center justify-center py-20">
                                <div className="text-center">
                                  <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-accent"></div>
                                  <p className="text-sm text-muted-foreground">{t('header.search.loading')}</p>
                                </div>
                              </div>
                            ) : (
                              <div className="rounded-2xl border border-border/70 bg-white px-4 py-10 text-center">
                                <MagnifyingGlass className="mx-auto h-10 w-10 text-muted-foreground/35" />
                                <p className="mt-3 text-sm font-medium text-foreground">
                                  {t('header.search.no_results', { query: normalizedSearchQuery })}
                                </p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            <div className={cn(isMobile ? 'border-b border-border/60 pb-6' : 'col-span-3 border-r pr-8')}>
                              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-5 px-2">
                                {t('header.search.most_searched_title')}
                              </h3>
                              <div className="space-y-1">
                                {resolvedMostSearched.map((termLabel) => {
                                  return (
                                  <button
                                    key={termLabel}
                                    onClick={() => {
                                      setSearchQuery(termLabel)
                                      handleSearchSubmit(termLabel)
                                    }}
                                    className="w-full text-left px-4 py-3.5 rounded-lg transition-all flex items-center gap-3 group hover:bg-muted text-foreground"
                                  >
                                    <div className="text-accent">
                                      <MagnifyingGlass className="h-5 w-5" />
                                    </div>
                                    <span className="font-medium flex-1">{termLabel}</span>
                                  </button>
                                  )
                                })}
                              </div>
                            </div>

                            <div className={cn(isMobile ? 'min-h-0' : 'col-span-9 min-h-[300px]')}>
                              {autocompleteSuggestions.length > 0 ? (
                                <div>
                                  <div className="flex items-center gap-3 mb-6">
                                    <div className="text-accent">
                                      <MagnifyingGlass className="h-6 w-6" />
                                    </div>
                                    <h3 className="text-lg font-bold text-foreground">
                                      {t('header.search.results_for', { query: searchQuery })}
                                    </h3>
                                    {isAiSearchAvailable ? (
                                      <button
                                        type="button"
                                        onClick={openAiSearchWithQuery}
                                        className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent/10 hover:text-accent"
                                      >
                                        <Sparkle className="h-3.5 w-3.5" />
                                        {t('ai_search.use')}
                                      </button>
                                    ) : null}
                                  </div>
                                  <div className={cn('grid gap-4', isMobile ? 'grid-cols-1' : 'grid-cols-2')}>
                                    {autocompleteSuggestions.map((product) => (
                                      <a
                                        key={product.id}
                                        href={buildProductHref(product)}
                                        className="block w-full text-left p-4 rounded-lg border-2 border-border hover:border-accent hover:bg-accent/5 transition-all group"
                                        onClick={(event) => {
                                          handleLinkClick(event, () => {
                                            addToSearchHistory(searchQuery)
                                            setShowSearchDropdown(false)
                                            onProductClick(product)
                                          })
                                        }}
                                      >
                                        <div className="flex gap-4">
                                          <div className="w-20 h-20 bg-muted/50 flex items-center justify-center overflow-hidden rounded-lg flex-shrink-0">
                                            {product.image ? (
                                              <img
                                                src={product.image}
                                                alt={product.name}
                                                width={80}
                                                height={80}
                                                loading="lazy"
                                                decoding="async"
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                              />
                                            ) : (
                                              <Package className="h-8 w-8 text-muted-foreground" />
                                            )}
                                          </div>
                                          <div className="flex min-w-0 flex-1 flex-col">
                                            <h5 className="text-sm font-semibold text-foreground line-clamp-2 mb-2 group-hover:text-accent transition-colors">
                                              {product.name}
                                            </h5>
                                            <div className="mb-1 flex flex-wrap items-center gap-2">
                                              <span className="text-xs text-muted-foreground font-mono">
                                                {product.code}
                                              </span>
                                              {product.brand && (
                                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 rounded-full">
                                                  {product.brand}
                                                </Badge>
                                              )}
                                            </div>
                                            <SearchProductResultStatus product={product} />
                                          </div>
                                        </div>
                                      </a>
                                    ))}
                                  </div>
                                  {(hasMoreAutocompleteSuggestions || isLoadingMoreSuggestions) ? (
                                    <div
                                      ref={autocompleteLoadMoreRef}
                                      className="flex min-h-10 items-center justify-center py-3"
                                    >
                                      {isLoadingMoreSuggestions ? (
                                        <CircleNotch className="h-5 w-5 animate-spin text-accent" />
                                      ) : null}
                                    </div>
                                  ) : null}
                                </div>
                              ) : isLoadingSuggestions ? (
                                <div className="flex items-center justify-center py-20">
                                  <div className="text-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
                                    <p className="text-sm text-muted-foreground">{t('header.search.loading')}</p>
                                  </div>
                                </div>
                              ) : (
                              <div className={cn('grid gap-8', isMobile ? 'grid-cols-1' : 'grid-cols-2')}>
                                <div>
                                  <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center gap-3">
                                      <div className="text-accent">
                                        <Clock className="h-6 w-6" />
                                      </div>
                                      <h3 className="text-lg font-bold text-foreground">{t('header.search.history_title')}</h3>
                                    </div>
                                    {searchHistory && searchHistory.length > 0 && (
                                      <button
                                        onClick={clearSearchHistory}
                                        className="text-xs text-muted-foreground hover:text-foreground transition-colors font-medium"
                                      >
                                        {t('actions.clear')}
                                      </button>
                                    )}
                                  </div>
                                  {searchHistory && searchHistory.length > 0 ? (
                                    <div className="space-y-2">
                                      {searchHistory.map((query, index) => (
                                        <div
                                          key={index}
                                          className="group flex items-center gap-3 p-4 rounded-lg border-2 border-border hover:border-accent hover:bg-accent/5 transition-all"
                                        >
                                          <button
                                            className="flex-1 text-left font-medium text-foreground group-hover:text-accent transition-colors"
                                            onClick={() => {
                                              setSearchQuery(query)
                                              handleSearchSubmit(query)
                                            }}
                                          >
                                            {query}
                                          </button>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              removeFromSearchHistory(query)
                                            }}
                                            aria-label={t('actions.remove')}
                                            title={t('actions.remove')}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 hover:text-destructive"
                                          >
                                            <X className="h-4 w-4" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="text-center py-12 text-muted-foreground">
                                      <Clock className="h-12 w-12 mx-auto mb-3 opacity-20" />
                                      <p className="text-sm">{t('header.search.history_empty')}</p>
                                    </div>
                                  )}
                                </div>

                                <div>
                                  <div className="flex items-center gap-3 mb-6">
                                    <div className="text-accent">
                                      <Package className="h-6 w-6" />
                                    </div>
                                    <h3 className="text-lg font-bold text-foreground">
                                      {t('header.search.recent_title')}
                                    </h3>
                                  </div>
                                  {recentlyViewed && recentlyViewed.some(Boolean) ? (
                                    <div className="space-y-3">
                                      {recentlyViewed
                                        .filter(Boolean)
                                        .slice(0, 5)
                                        .map((product) => (
                                        <a
                                          key={product.id}
                                          href={buildProductHref(product)}
                                          className="block w-full text-left p-4 rounded-lg border-2 border-border hover:border-accent hover:bg-accent/5 transition-all group"
                                          onClick={(event) => {
                                            handleLinkClick(event, () => {
                                              setShowSearchDropdown(false)
                                              onProductClick(product)
                                            })
                                          }}
                                        >
                                          <div className="flex gap-4">
                                            <div className="w-16 h-16 bg-muted/50 flex items-center justify-center overflow-hidden rounded-lg flex-shrink-0">
                                              {product.image ? (
                                                <img
                                                  src={product.image}
                                                  alt={product.name}
                                                  width={64}
                                                  height={64}
                                                  loading="lazy"
                                                  decoding="async"
                                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                                />
                                              ) : (
                                                <Package className="h-8 w-8 text-muted-foreground" />
                                              )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <h5 className="text-sm font-semibold text-foreground line-clamp-2 mb-1 group-hover:text-accent transition-colors">
                                                {product.name}
                                              </h5>
                                              <span className="text-xs text-muted-foreground font-mono block">
                                                {product.code}
                                              </span>
                                            </div>
                                          </div>
                                        </a>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="text-center py-12 text-muted-foreground">
                                      <Package className="h-12 w-12 mx-auto mb-3 opacity-20" />
                                      <p className="text-sm">{t('header.search.recent_empty')}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className={cn('flex items-center', isMobile ? 'order-2 ml-auto gap-2' : 'gap-3')}>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'relative text-header-foreground hover:bg-white/10 hover:text-header-foreground rounded-full transition-all',
                isMobile ? 'h-10 w-10' : 'h-11 w-11',
              )}
              onMouseEnter={onCartPrefetch}
              onFocus={onCartPrefetch}
              onTouchStart={onCartPrefetch}
              onClick={onCartClick}
              aria-label={t('header.account.cart')}
              title={t('header.account.cart')}
            >
              <ShoppingCart className="h-6 w-6" />
              {cartCount > 0 && (
                <Badge className="absolute -right-1 -top-1 h-5 min-w-5 px-1 flex items-center justify-center bg-accent text-accent-foreground rounded-full font-semibold text-xs">
                  {cartCount}
                </Badge>
              )}
            </Button>

            {isLoggedIn ? (
              isMobile ? (
                <Sheet open={showMobileAccountSheet} onOpenChange={setShowMobileAccountSheet}>
                  <SheetTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={mobileAccountTriggerClassName}
                      aria-label={t('header.account.title')}
                    >
                      <User className="h-5 w-5" weight="fill" />
                      {accountNotificationCount > 0 ? (
                        <Badge className={mobileAccountTriggerBadgeClassName}>
                          {accountNotificationCount > 9 ? '9+' : accountNotificationCount}
                        </Badge>
                      ) : null}
                    </Button>
                  </SheetTrigger>
                  <SheetContent
                    side="bottom"
                    className="max-h-[85vh] rounded-t-[28px] border-t border-slate-200 bg-white p-0"
                  >
                    <div className="scrollbar-hover-quiet max-h-[85vh] overflow-y-auto px-4 pb-5 pt-8">
                      <div className="px-1">
                        <p className="line-clamp-1 text-[17px] font-semibold leading-tight text-slate-950">
                          {loggedAccountName}
                        </p>
                        {loggedAccountCorporateName ? (
                          <p className="mt-1 line-clamp-1 text-sm font-medium text-slate-700">
                            {loggedAccountCorporateName}
                          </p>
                        ) : null}
                        <p className="mt-1 line-clamp-1 text-sm text-slate-500">
                          {loggedAccountDocument}
                        </p>
                        {indicatorDelegation ? (
                          <div className="mt-4 border-y border-sky-200 bg-sky-50 px-3 py-3 text-sky-950">
                            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-sky-700">Atendendo como</p>
                            <p className="mt-1 truncate text-sm font-semibold">{indicatorDelegation.clientName}</p>
                            <p className="mt-0.5 text-xs text-sky-700">{delegatedClientDocument}</p>
                            <button
                              type="button"
                              onClick={() => {
                                closeAccountMenu()
                                onEndIndicatorDelegation?.()
                              }}
                              className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-sky-800 hover:text-sky-950"
                            >
                              <SignOut className="h-4 w-4" />
                              Encerrar atendimento
                            </button>
                          </div>
                        ) : null}
                      </div>

                      <div className="mt-5 space-y-2">
                        {shortcutAccountItems.map((item) => renderAccountSheetItem(item))}
                      </div>

                      {shouldShowAccountNotifications ? (
                        <>
                          <div className="my-4 h-px bg-slate-200" />

                          <div>
                            <p className="px-1 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                              <span className="inline-flex items-center gap-1.5">
                                <Bell className="h-3.5 w-3.5" weight="fill" />
                                <span>{t('header.account.notifications.title')}</span>
                              </span>
                            </p>

                            {visibleAccountNotifications.length > 0 ? (
                              <div className="space-y-1.5">
                                {visibleAccountNotifications.map((notification) => renderAccountNotificationSheetCard(notification))}
                              </div>
                            ) : (
                              <div className="rounded-[18px] border border-dashed border-slate-200 bg-white px-4 py-3">
                                <p className="text-sm font-semibold text-slate-700">
                                  {t('header.account.notifications.empty_title')}
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {t('header.account.notifications.empty_description')}
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="my-4 h-px bg-slate-200" />
                        </>
                      ) : (
                        <div className="my-4 h-px bg-slate-200" />
                      )}

                      <button
                        type="button"
                        onClick={handleAccountLogout}
                        className="sticky bottom-0 flex w-full items-center gap-3 rounded-[18px] border border-slate-200/80 bg-white px-4 py-3 text-left text-slate-800 shadow-[0_-12px_24px_-22px_rgba(15,23,42,0.45)] transition-colors hover:bg-slate-50"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-slate-50 text-slate-500">
                          <SignOut className="h-4 w-4 text-current" />
                        </span>
                        <span className="text-[15px] font-semibold">{t('actions.sign_out')}</span>
                      </button>
                    </div>
                  </SheetContent>
                </Sheet>
              ) : (
                <DropdownMenu open={showAccountMenu} onOpenChange={setShowAccountMenu}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className={accountTriggerClassName}
                      aria-label={t('header.account.title')}
                    >
                      <span className={accountTriggerBellClassName} style={accountTriggerBellStyle}>
                        {shouldShowAccountNotifications ? (
                          <Bell className="h-[18px] w-[18px]" weight="fill" />
                        ) : (
                          <User className="h-[18px] w-[18px]" weight="fill" />
                        )}
                        {accountNotificationCount > 0 ? (
                          <Badge className={accountTriggerBadgeClassName}>
                            {accountNotificationCount > 9 ? '9+' : accountNotificationCount}
                          </Badge>
                        ) : null}
                      </span>
                      <span className="hidden lg:inline">{t('header.account.title')}</span>
                      <CaretDown className="account-caret h-4 w-4 transition-transform" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    sideOffset={12}
                    className="scrollbar-hover-quiet max-h-[calc(100vh-5.5rem)] w-[25rem] max-w-[calc(100vw-1rem)] overflow-y-auto overscroll-contain rounded-[16px] border border-slate-200/90 bg-white p-0 shadow-[0_24px_64px_-18px_rgba(15,23,42,0.24)]"
                  >
                    <div className="px-4 py-3.5">
                      <div className="px-1">
                        <a
                          href={VIEW_PATHS.dashboard}
                          className="block rounded-[14px] px-1 py-1.5 transition-colors hover:bg-slate-50"
                          onClick={(event) => handleLinkClick(event, () => {
                            closeAccountMenu()
                            onViewChange('dashboard')
                          })}
                        >
                          <div className="flex items-center gap-3">
                            <div className="shrink-0">
                              <Suspense
                                fallback={<div className="h-14 w-14 rounded-full border border-slate-200 bg-slate-50" />}
                              >
                                <AccountCustomerLogo
                                  customerId={indicatorDelegation?.indicatorId ?? accountProfile?.entidadeId ?? null}
                                  name={loggedAccountCorporateName || loggedAccountName}
                                  size="sm"
                                  fillImage
                                  elevated={false}
                                />
                              </Suspense>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="line-clamp-1 text-[15px] font-semibold leading-tight text-slate-950">
                                {loggedAccountName}
                              </p>
                              {loggedAccountCorporateName ? (
                                <p className="mt-1 line-clamp-1 text-[13px] font-medium leading-tight text-slate-700">
                                  {loggedAccountCorporateName}
                                </p>
                              ) : null}
                              <p className="mt-1 line-clamp-1 text-xs text-slate-500">
                                {loggedAccountDocument}
                              </p>
                            </div>
                          </div>
                        </a>
                      </div>

                      {indicatorDelegation ? (
                        <div className="mt-2 border-y border-sky-200 bg-sky-50 px-3 py-3 text-sky-950">
                          <div className="min-w-0">
                            <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-sky-700">Atendendo como</p>
                            <p className="mt-1 truncate text-sm font-semibold">{indicatorDelegation.clientName}</p>
                            <p className="mt-0.5 text-xs text-sky-700">{delegatedClientDocument}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              closeAccountMenu()
                              onEndIndicatorDelegation?.()
                            }}
                            className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-sky-800 hover:text-sky-950"
                          >
                            <SignOut className="h-4 w-4" />
                            Encerrar atendimento
                          </button>
                        </div>
                      ) : null}

                      <div className="pt-3.5">
                        <div className="space-y-0.5">
                          {shortcutAccountItems.map((item) => renderAccountMenuItem(item))}
                        </div>
                      </div>

                      {shouldShowAccountNotifications ? (
                        <>
                          <DropdownMenuSeparator className="my-2.5" />

                          <div>
                            <DropdownMenuLabel className="px-0 pb-1.5 text-[9px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                              <span className="inline-flex items-center gap-1.5">
                                <Bell className="h-3.5 w-3.5" weight="fill" />
                                <span>{t('header.account.notifications.title')}</span>
                              </span>
                            </DropdownMenuLabel>

                            {visibleAccountNotifications.length > 0 ? (
                              <div className="space-y-1.5">
                                {visibleAccountNotifications.map((notification) => renderAccountNotification(notification))}
                              </div>
                            ) : (
                              <div className="rounded-[14px] border border-dashed border-slate-200 bg-white px-4 py-3">
                                <p className="text-sm font-semibold text-slate-700">
                                  {t('header.account.notifications.empty_title')}
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {t('header.account.notifications.empty_description')}
                                </p>
                              </div>
                            )}
                          </div>

                          <DropdownMenuSeparator className="my-3" />
                        </>
                      ) : (
                        <DropdownMenuSeparator className="my-3" />
                      )}

                      <div className="sticky bottom-0 bg-white py-1 shadow-[0_-12px_24px_-22px_rgba(15,23,42,0.45)]">
                        <DropdownMenuItem
                          onClick={handleAccountLogout}
                          onSelect={(event) => {
                            event.preventDefault()
                            handleAccountLogout()
                          }}
                          className="cursor-pointer rounded-[12px] border border-transparent bg-white px-3 py-2 text-xs font-semibold text-slate-700 outline-none data-[highlighted]:border-red-100 data-[highlighted]:bg-red-50 data-[highlighted]:text-red-600 data-[highlighted]:[&_span[data-role=icon]]:bg-red-100 data-[highlighted]:[&_span[data-role=icon]]:text-red-600"
                        >
                          <span data-role="icon" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-slate-50 text-slate-500 transition-colors duration-200">
                            <SignOut className="h-4 w-4 text-current" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <span className="block truncate">{t('actions.sign_out')}</span>
                          </div>
                        </DropdownMenuItem>
                      </div>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              )
            ) : (
              <>
                <Button
                  variant="ghost"
                  className="text-header-foreground hover:bg-white/10 hover:text-header-foreground h-11 px-4 gap-2 rounded-lg font-medium hidden sm:flex"
                  asChild
                >
                  <a
                    href={VIEW_PATHS.login}
                    onClick={(event) => handleLinkClick(event, onLoginClick)}
                  >
                    <User className="h-5 w-5" />
                    <span>{t('actions.sign_in')}</span>
                  </a>
                </Button>
                <Button
                  className="hidden h-11 rounded-lg bg-accent px-6 font-semibold text-accent-foreground shadow-lg hover:bg-accent/90 hover:text-accent-foreground sm:flex"
                  asChild
                >
                  <a
                    href={VIEW_PATHS.register}
                    onClick={(event) =>
                      handleLinkClick(event, () => onViewChange('register'))
                    }
                  >
                    {t('actions.create_account')}
                  </a>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-header-foreground hover:bg-white/10 hover:text-header-foreground h-10 w-10 rounded-lg sm:hidden"
                  asChild
                >
                  <a
                    href={VIEW_PATHS.login}
                    onClick={(event) => handleLinkClick(event, onLoginClick)}
                    aria-label={t('actions.sign_in')}
                  >
                    <User className="h-6 w-6" />
                  </a>
                </Button>
              </>
            )}
          </div>
        </div>

      </div>
    </header>

    <div className="border-b border-border/60 bg-white">
      <div className="container mx-auto px-4">
        <nav
          aria-label={t('header.quick_links.label')}
          className="overflow-hidden"
        >
          <div ref={quickLinksLayoutRef} className="relative flex min-w-full items-center gap-6 overflow-hidden">
            <ul className="flex min-w-0 flex-1 items-center gap-6 overflow-hidden">
              {visibleBaseQuickLinkItems.map((item) => (
                <li key={item.key} className={cn('shrink-0', item.itemClassName)}>
                  {renderBaseQuickLink(item)}
                </li>
              ))}
              {overflowBaseQuickLinkItems.length > 0 ? (
                <li className="shrink-0">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          moreOptionsTriggerClassName,
                          'data-[state=open]:border-accent data-[state=open]:text-foreground'
                        )}
                        aria-label={moreOptionsLabel}
                      >
                        <DotsThree className="h-4 w-4 shrink-0" />
                        <span className="hidden sm:inline">{moreOptionsLabel}</span>
                        <CaretDown className="h-3.5 w-3.5 shrink-0" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      sideOffset={8}
                      className="w-[min(18rem,calc(100vw-2rem))] rounded-[18px] border border-slate-200 bg-white p-2 shadow-xl"
                    >
                      {overflowBaseQuickLinkItems.map((item) => renderOverflowQuickLink(item))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </li>
              ) : null}
            </ul>

            {showVehiclePlateFilter ? (
              <div ref={vehicleQuickLinkRef} className="shrink-0">
                <Popover open={showVehicleSearchPopover} onOpenChange={(open) => {
                  setShowVehicleSearchPopover(open)
                  if (open) {
                    closeSearchDropdown()
                    closeMegaMenus()
                  }
                }}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-12 items-center gap-2 border-b-2 border-transparent px-0 text-sm font-medium whitespace-nowrap text-foreground/75 transition-colors hover:border-border hover:text-foreground focus-visible:border-accent focus-visible:text-foreground focus-visible:outline-none data-[state=open]:border-accent data-[state=open]:text-foreground"
                    >
                      <MagnifyingGlass className="h-4 w-4 shrink-0" />
                      <span>{t('header.quick_links.vehicle_search_menu.label')}</span>
                      <CaretDown className="h-3.5 w-3.5 shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    sideOffset={8}
                    className="w-[min(calc(100vw-2rem),380px)] rounded-xl border border-slate-200 bg-white p-4 shadow-xl"
                  >
                    <VehicleSearchPanel
                      categories={categories}
                      onVehicleSearch={handleHeaderVehicleSearch}
                      activeVehicleFilter={activeVehicleFilter}
                      enabled={showVehiclePlateFilter}
                      initialMode={initialVehicleSearchMode}
                      variant="dropdown"
                      onSubmitComplete={closeVehicleSearchPopover}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            ) : null}

            <div inert className="pointer-events-none absolute left-0 top-0 -z-10 h-0 overflow-hidden opacity-0">
              <ul ref={quickLinksMeasureListRef} className="flex items-center gap-6">
                {baseQuickLinkItems.map((item) => (
                  <li
                    key={`measure-${item.key}`}
                    ref={(node) => {
                      quickLinkMeasureItemRefs.current[item.key] = node
                    }}
                    className={cn('shrink-0', item.itemClassName)}
                  >
                    {renderBaseQuickLink(item)}
                  </li>
                ))}
              </ul>
              <button
                ref={moreOptionsMeasureRef}
                type="button"
                className={moreOptionsTriggerClassName}
                tabIndex={-1}
              >
                <DotsThree className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline">{moreOptionsLabel}</span>
                <CaretDown className="h-3.5 w-3.5 shrink-0" />
              </button>
            </div>

            {(showCommunityLinks || showAppsMenu || !isMobile) ? (
            <div ref={appsQuickLinkRef} className="ml-auto flex shrink-0 items-center gap-3 sm:gap-5">
              {!isMobile ? (
                <button
                  type="button"
                  onPointerEnter={onSalesCampaignPrefetch}
                  onFocus={onSalesCampaignPrefetch}
                  onClick={() => {
                    onSalesCampaignPrefetch?.()
                    onViewChange('sales-campaign')
                  }}
                  aria-current={currentView === 'sales-campaign' ? 'page' : undefined}
                  className="inline-flex h-8 items-center justify-center rounded-lg bg-emerald-600 px-4 text-xs font-semibold whitespace-nowrap text-white shadow-sm transition-colors hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 sm:text-sm"
                >
                  {t('header.quick_links.sales_campaign')}
                </button>
              ) : null}
              {showCommunityLinks ? (
                <>
                  <a href={VIEW_PATHS.blog} className="inline-flex h-12 items-center border-b-2 border-transparent text-xs font-semibold whitespace-nowrap text-foreground/75 transition-colors hover:border-accent hover:text-foreground focus-visible:border-accent focus-visible:outline-none sm:text-sm">
                    Blog
                  </a>
                  {showTechnicalTablesLink ? (
                    <a href={`${VIEW_PATHS.blog}#tabelas-tecnicas`} className="inline-flex h-12 items-center border-b-2 border-transparent text-xs font-semibold whitespace-nowrap text-foreground/75 transition-colors hover:border-accent hover:text-foreground focus-visible:border-accent focus-visible:outline-none sm:text-sm">
                      Tabelas técnicas
                    </a>
                  ) : null}
                </>
              ) : null}
              {showAppsMenu ? (
              <div>
              {isMobile ? (
                <Sheet open={showMobileAppsSheet} onOpenChange={(open) => {
                  setShowMobileAppsSheet(open)
                  if (!open) setAppSearchQuery('')
                }}>
                  <SheetTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-12 items-center gap-2 border-b-2 border-transparent px-0 text-sm font-medium whitespace-nowrap text-foreground/75 transition-colors hover:border-border hover:text-foreground focus-visible:border-accent focus-visible:text-foreground focus-visible:outline-none"
                    >
                      <SquaresFour className="h-4 w-4 shrink-0" />
                      <span>{t('header.quick_links.apps')}</span>
                    </button>
                  </SheetTrigger>
                  <SheetContent
                    side="bottom"
                    className="max-h-[85vh] rounded-t-[28px] border-t border-slate-200 bg-white p-0"
                  >
                    <div className="overflow-y-auto px-4 pb-6 pt-8">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">
                          {t('header.quick_links.apps_menu.sidebar_title')}
                        </p>
                        <p className="mt-2 text-sm leading-6 text-slate-500">
                          {t('header.quick_links.apps_menu.sidebar_card_description')}
                        </p>
                      </div>
                      {renderAppSearchInput('mobile-app-search', 'mt-4')}

                      <div className="mt-5 flex gap-2 overflow-x-auto pb-1">
                        {appMenuSegments.map((segment) => {
                          const isActive = !isAppSearchActive && activeAppSegment === segment.key
                          return (
                            <button
                              key={`mobile-app-segment-${segment.key}`}
                              type="button"
                              aria-pressed={isActive}
                              onClick={() => selectAppSegment(segment.key)}
                              className={cn(
                                'shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-colors',
                                isActive
                                  ? 'bg-orange-50 text-orange-800 ring-1 ring-inset ring-orange-200'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              )}
                            >
                              {t(`header.quick_links.apps_menu.segments.${segment.key}`)}
                            </button>
                          )
                        })}
                      </div>

                      <div className="mt-5 space-y-2">
                        {isAppSearchActive ? (
                          <p className="pb-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                            {t('header.quick_links.apps_menu.search_results_title')} · {activeAppLinks.length}
                          </p>
                        ) : null}
                        {activeAppLinks.length > 0
                          ? activeAppLinks.map((item) => renderMobileAppLink(item))
                          : <p className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">{t('header.quick_links.apps_menu.search_empty')}</p>}
                      </div>

                      <a href={VIEW_PATHS.apps}
                        onClick={(event) => handleLinkClick(event, () => { closeAppsMenu(); onViewChange('apps') })}
                        className="mt-4 flex items-center justify-between rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm font-semibold text-orange-800">
                        {t('apps_page.view_all')} <CaretRight className="h-4 w-4" />
                      </a>

                      {!isAppSearchActive ? (
                        <div className="mt-5 flex items-center gap-4 rounded-[18px] border border-slate-200/80 bg-[linear-gradient(135deg,#FFFFFF_0%,#F8FAFC_100%)] px-4 py-4">
                          <div className="flex shrink-0 items-center">
                            <span className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-[12px] border border-slate-200 bg-white">
                              <img
                                src={universidadeIcon}
                                alt={t('header.quick_links.university')}
                                width={48}
                                height={48}
                                loading="lazy"
                                decoding="async"
                                className="h-9 w-9 object-contain"
                              />
                            </span>
                            <span className="-ml-3 flex h-12 w-12 items-center justify-center overflow-hidden rounded-[12px] border border-white bg-white shadow-sm">
                              <img
                                src={consultaengIcon}
                                alt={t('header.quick_links.consultaeng')}
                                width={48}
                                height={48}
                                loading="lazy"
                                decoding="async"
                                className="h-full w-full object-cover"
                              />
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-950">
                              {t('header.quick_links.apps_menu.banner_title')}
                            </p>
                            <p className="mt-1 text-sm leading-6 text-slate-500">
                              {t('header.quick_links.apps_menu.banner_description')}
                            </p>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </SheetContent>
                </Sheet>
              ) : (
                <DropdownMenu onOpenChange={(open) => {
                  if (!open) setAppSearchQuery('')
                }}>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-12 items-center gap-2 border-b-2 border-transparent px-0 text-sm font-medium whitespace-nowrap text-foreground/75 transition-colors hover:border-border hover:text-foreground focus-visible:border-accent focus-visible:text-foreground focus-visible:outline-none data-[state=open]:border-accent data-[state=open]:text-foreground"
                    >
                      <SquaresFour className="h-4 w-4 shrink-0" />
                      <span>{t('header.quick_links.apps')}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="max-h-[calc(100vh-5.5rem)] w-[min(92vw,900px)] overflow-y-auto rounded-[20px] border border-slate-200 bg-white p-0 shadow-[0_28px_70px_rgba(15,23,42,0.18)]"
                  >
                    <div className="grid gap-0 md:grid-cols-[208px_minmax(0,1fr)]">
                      <div className="flex flex-col border-r border-slate-200 bg-slate-50 p-4">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">
                          {t('header.quick_links.apps_menu.sidebar_title')}
                        </p>
                        <div className="mt-4 space-y-1">
                          {appMenuSegments.map((segment) => {
                            const isActive = !isAppSearchActive && activeAppSegment === segment.key
                            return (
                              <button
                                key={segment.key}
                                type="button"
                                aria-pressed={isActive}
                                onClick={() => selectAppSegment(segment.key)}
                                className={cn(
                                  'flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400',
                                  isActive
                                    ? 'border-orange-200 bg-white text-slate-950 shadow-[0_4px_12px_rgba(15,23,42,0.05)]'
                                    : 'border-transparent text-slate-600 hover:bg-white hover:text-slate-950'
                                )}
                              >
                                <span>{t(`header.quick_links.apps_menu.segments.${segment.key}`)}</span>
                                {isActive ? (
                                  <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                                ) : null}
                              </button>
                            )
                          })}
                        </div>

                        <div className="mt-auto rounded-xl border border-slate-200 bg-white p-3.5">
                          <p className="text-sm font-semibold text-slate-950">
                            {t('header.quick_links.apps_menu.sidebar_card_title')}
                          </p>
                          <p className="mt-1.5 text-xs leading-5 text-slate-500">
                            {t('header.quick_links.apps_menu.sidebar_card_description')}
                          </p>
                        </div>
                      </div>

                      <div className="flex min-w-0 flex-col px-6 py-5">
                        <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-4">
                          <div className="min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-orange-600">
                              {t('header.quick_links.apps')}
                            </p>
                            <h3 className="mt-1 truncate text-lg font-semibold text-slate-950">
                              {isAppSearchActive
                                ? t('header.quick_links.apps_menu.search_results_title')
                                : activeAppSegment === 'featured'
                                ? t('header.quick_links.apps_menu.featured_title')
                                : t(`header.quick_links.apps_menu.segments.${activeAppSegment}`)}
                            </h3>
                          </div>
                          {renderAppSearchInput('desktop-app-search', 'w-[245px] shrink-0')}
                        </div>

                        <div className="mt-4 grid grid-cols-2 gap-3">
                          {activeAppLinks.length > 0
                            ? activeAppLinks.map((item, index) => renderDesktopAppLink(
                              item,
                              index === 0 && ((!isAppSearchActive && activeAppSegment === 'featured') || activeAppLinks.length % 2 === 1),
                            ))
                            : <p className="col-span-2 rounded-xl border border-dashed border-slate-200 px-6 py-10 text-center text-sm text-slate-500">{t('header.quick_links.apps_menu.search_empty')}</p>}
                        </div>

                        <a href={VIEW_PATHS.apps}
                          onClick={(event) => handleLinkClick(event, () => { closeSearchDropdown(); closeMegaMenus(); onViewChange('apps') })}
                          className="mt-4 inline-flex items-center gap-2 self-end text-sm font-semibold text-orange-700 hover:text-orange-900">
                          {t('apps_page.view_all')} <CaretRight className="h-4 w-4" />
                        </a>

                        {!isAppSearchActive ? (
                          <div className="mt-5 flex items-center justify-between gap-4 rounded-xl bg-slate-50 px-4 py-3">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-950">
                                {t('header.quick_links.apps_menu.banner_title')}
                              </p>
                              <p className="mt-0.5 text-xs leading-5 text-slate-500">
                                {t('header.quick_links.apps_menu.banner_description')}
                              </p>
                            </div>
                            <div className="flex shrink-0 items-center">
                              <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-[10px] border border-slate-200 bg-white">
                                <img
                                  src={universidadeIcon}
                                  alt={t('header.quick_links.university')}
                                  width={40}
                                  height={40}
                                  loading="lazy"
                                  decoding="async"
                                  className="h-8 w-8 object-contain"
                                />
                              </span>
                              <span className="-ml-2 flex h-10 w-10 items-center justify-center overflow-hidden rounded-[10px] border border-white bg-white shadow-sm">
                                <img
                                  src={consultaengIcon}
                                  alt={t('header.quick_links.consultaeng')}
                                  width={40}
                                  height={40}
                                  loading="lazy"
                                  decoding="async"
                                  className="h-full w-full object-cover"
                                />
                              </span>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              </div>
              ) : null}
            </div>
            ) : null}
          </div>
        </nav>
      </div>
    </div>

    <Suspense fallback={null}>
      <CategoryMegaMenu
        categories={categories}
        isLoading={isMenuLoading}
        open={showMegaMenu}
        onOpenChange={setShowMegaMenu}
        onCategorySelect={handleCategorySelect}
        isMobile={false}
      />

      <CategoryMegaMenu
        categories={categories}
        isLoading={isMenuLoading}
        open={showMobileCategoryMenu}
        onOpenChange={setShowMobileCategoryMenu}
        onCategorySelect={handleCategorySelect}
        isMobile={true}
      />
    </Suspense>
    </>
  )
}
